import { Component, Inject, OnInit, Input, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MyApplicationsService } from '@app/pages/my-application/my-app-service.service';
import { HttpService } from '@core/services/http.service';
import { forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs';
import { LocalService } from '@app/@core/services/local.service';
import { DataStoreService } from '@pics-core/dynamic';
import { RegistrationService } from '@app/registration/registration.service';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { AppConstants } from '@app/@core/entities/app-constants';
import { LoaderService } from '@app/@core/services/loader.service';
import { CommonService } from '@app/shared/services/common.service';
import { AlertService } from '@app/@core/services/alert.service';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';

@Component({
  standalone: false,
  selector: 'app-application-type-dialog',
  templateUrl: './application-type-dialog.component.html',
  styleUrls: ['./application-type-dialog.component.scss']
})
export class ApplicationTypeDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  errorMessage: string = '';
  errorMessages: any = {}; // Error messages from API
  placeholderMessage: Record<string, string> = {}; // Placeholder messages from API
  selectedType: string = '';
  selectTypeValue: any[] = [];
  lookupData: any[] = [];
  @Input() applicationTypes: any[] = [];
  applicationStatusData:any[] = [];
  personData: any;
  navigationData: any[] = [];
  licenseKindData:any[] = [];
  endorsementData:any[] = [];
  suffixData:any[] = [];
  res_activeversionId: any;
  userId:any;
  noPersonData:boolean = true;

  // Properties for renew condition check
  dashboardData: any = null;
  applicationsData: any[] = []; // Store applications data from applications API (contains cd_application_status)
  showRenewOption: boolean = false;
  applicationTypesReady: boolean = false; // Defer showing list until condition check finishes
  private destroy$ = new Subject<void>();

  // Properties for qualifying questions
  showQualifyingQuestions: boolean = false;
  qualifyingQuestions: string[] = [];
  qualifyingAnswers: ('yes' | 'no' | 'not applicable' | null)[] = [];
  qualifyingErrors: string[] = [];
  qualifyingGlobalError: string | null = null;
  qualifyingQuestionKeys: string[] = []; // Store the key for each question (e.g., "3", "5", "6")
  visibleQuestionIndices: number[] = []; // Track which questions are currently visible
  displayApplicationType: string = ''; // For RCR flow - stores display value for banner/session
  oldApplicationId: string | null = null; // For RCR flow - stores previous application_number (string like "A26-003081")
  oldApplicationIntegerId: number | null = null; // For RCR flow - stores previous application_id (integer like 5327 from applications API)
  validRcrApplicationId: string | null = null; // Stores the application_id from the valid RCR application found in checkRenewConditions
  mostRecentActiveAppId: number | null = null; // Integer id of the most recent active application — used as old_id when cloning in RCR flow
  licensePath: string | null = null; // For RCR flow - stores license_path value (e.g., '1' for Renewal)
  currentApplicationType: string = ''; // Track current application type being processed (e.g., 'Regular - Initial', 'Substitute')
  licenseRequirementKeys: Set<string> = new Set(); // Track question keys that are for license requirements
  licenseProvisionKeys: Set<string> = new Set(); // Track question keys that are for license provisions
  isCreatingApplication: boolean = false;
  licensePathLookup: any[] = [];

  // Properties for RCR business rules
  private rcrNonArlDays: number = 0; // Default value for Regular-Initial, Substitute, Emergency Substitute
  private rcrArlDays: number = 0; // Default value for ARL applications
  private rcrBusinessRulesLoaded: boolean = false; // Track if business rules have been loaded
  private pendingRenewConditionCheck: boolean = false; // Track if we need to check conditions after rules load

  // Properties for ARL program fields (question key '2')
  showArlProgramFields: boolean = false;
  arlProviderName: string | null = null;
  arlProgramArea: string | null = null;
  arlCompletionDate: string | null = null;
  arlProviderOptions: {key: string, value: string, optionaldata?: any}[] = [];
  arlProgramAreaOptions: {key: string, value: string}[] = [];

  // ARL field validation tracking
  arlProgramAreaError: string = '';
  arlCompletionDateError: string = '';
  arlProgramAreaTouched: boolean = false;
  arlCompletionDateTouched: boolean = false;

  shouldAllowSurrender: boolean = false;          
  shouldAllowExtend: boolean = false;             
  isExtendEnabled: boolean = false;                
  shouldAllowAddEndorsement: boolean = false;      
  shouldAllowResolveProvisions: boolean = false;
  savedConditionalOptions: any[] = [];  

  constructor(public dialogRef: MatDialogRef<ApplicationTypeDialogComponent>,
    private readonly appService: MyApplicationsService,
    private readonly httpService: HttpService,
    private readonly router: Router,
    private sessionStorage: LocalService,
    private dataStoreService : DataStoreService,
    private readonly registrationService: RegistrationService,
    private readonly loaderService: LoaderService,
    private readonly commonService: CommonService,
    private readonly alertService: AlertService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loaderService.show();
    // First load all application types, then check renew conditions to filter them if needed
    // (This ensures applicationTypes are available before filtering logic runs)
    this.loadDropdownData();
    // Load RCR business rules
    this.loadRcrBusinessRules();
  }

  ngAfterViewInit() {
    // Set focus on the close button when dialog opens for keyboard accessibility
    setTimeout(() => {
      const closeButton = document.querySelector('.close-btn') as HTMLElement;
      if (closeButton) {
        closeButton.focus();
      }
    }, 200);
  }

  private async loadDropdownData() {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=APPLICATION_TYPES'),
        this.httpService.get('/domain/lookup?requestType=LICENSE KIND'),
          this.httpService.get('/domain/lookup?requestType=ENDORSEMENT'),
          this.httpService.get('/domain/lookup?requestType=APPLICATION_STATUS'),
          this.httpService.get('/domain/lookup?requestType=SUFFIX'),
          this.httpService.get('/domain/lookup?requestType=MESSAGES'),
          this.httpService.get(API_ENDPOINTS.LOOKUP_LICENSE_PATH),
          this.httpService.get('/domain/lookup?requestType=ARL PROVIDER'),
          this.httpService.get('/domain/lookup?requestType=PLACEHOLDER MESSAGES'),
          this.httpService.get('/domain/lookup?requestType=PROGRAM PROVIDER')
    ]).subscribe({
      next: (item: any) => {
        // Desired order for radio buttons
        const desiredOrder = [
          'Regular - Initial',
          'Substitute',
          'Emergency Substitute',
          'ARL (Alternative Route to Licensure)',
          'Apprentice',
          'Name Change'
        ];
        // Sort applicationTypes to match desired order
        this.applicationTypes = desiredOrder
          .map(label => item[0].data.find(type => type.value === label))
          .filter(Boolean);
        this.licenseKindData = item[1].data;
        this.endorsementData = item[2].data;
        this.applicationStatusData = item[3].data;
        this.suffixData = item[4].data;
        this.licensePathLookup = item[6]?.data || [];

        // Process error messages
        if (item[5]?.data) {
          item[5].data.forEach((msg: any) => {
            this.errorMessages[msg.key] = msg.value;
          });
        }

        // Process ARL Provider Name options (filter by optionaldata not null, then sort alphabetically)
        this.arlProviderOptions = (item[7]?.data || [])
          .filter((option: any) => option.optionaldata != null)
          .sort((a: any, b: any) => a.value.localeCompare(b.value));

        // Process ARL Program Area options from PROGRAM PROVIDER API (sort alphabetically)
        const rawProgramProviders = Array.isArray(item[9]?.data)
          ? item[9].data
          : Array.isArray(item[9]?.data?.data)
            ? item[9].data.data
            : (Array.isArray(item[9]) ? item[9] : []);

        this.arlProgramAreaOptions = (rawProgramProviders || [])
          .map((d: any) => ({
            key: String(d?.key ?? d?.id ?? ''),
            value: String(d?.value ?? d?.name ?? '')
          }))
          .filter(d => d.key && d.value)
          .sort((a: any, b: any) => a.value.localeCompare(b.value));

        console.log('[ARL] Loaded Program Area options from PROGRAM PROVIDER:', this.arlProgramAreaOptions.length);

        // Process placeholder messages
        this.placeholderMessage = {};
        (item[8]?.data || []).forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) {
            this.placeholderMessage[msg.key] = msg.value;
          }
        });

        console.log('[ARL] Placeholder Messages loaded:', this.placeholderMessage);
        console.log('[ARL] P0124 value:', this.placeholderMessage['P0124']);
        console.log(this.applicationTypes);
        console.log('[ARL] Loaded Provider Name options:', this.arlProviderOptions.length);

        // After loading application types, check renew conditions to filter them if needed
        this.loadDashboardDataAndCheckConditions();
      },
      error: error => {
        console.error('Error loading dropdown data:', error);
        this.applicationTypesReady = true;
        this.loaderService.hide();
        this.cdr.detectChanges();
      }
    });
  }

  private async loadRequiredLookupData() {
    // Load only the lookup data needed for application creation
    // (used when applicationTypes is already provided by parent)
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=LICENSE KIND'),
      this.httpService.get('/domain/lookup?requestType=ENDORSEMENT'),
      this.httpService.get('/domain/lookup?requestType=APPLICATION_STATUS'),
      this.httpService.get('/domain/lookup?requestType=SUFFIX')
    ]).subscribe({
      next: (item: any) => {
        this.licenseKindData = item[0].data;
        this.endorsementData = item[1].data;
        this.applicationStatusData = item[2].data;
        this.suffixData = item[3].data;
        console.log('Required lookup data loaded for license flow');
      },
      error: error => {
        console.error('Error loading required lookup data:', error);
      }
    });
  }

  // loadData(){
  //   this.appService.getLookupData().subscribe(data => {
  //     this.lookupData = data;
  //   });
  // }

  // ====================================================================================
  // COMMENTED OUT: Helper methods for dynamic question expansion
  // These were used to fetch and process License Requirements, License Provisions, and Professional Development
  // Now using static questions from API instead
  // ====================================================================================

  // // Helper method to replace placeholders in questions with actual data
  // private replacePlaceholders(question: string, replacements: { [key: string]: string }): string {
  //   console.log('[PLACEHOLDER] Replacing placeholders...');
  //   console.log('[PLACEHOLDER] Original question:', question);
  //   console.log('[PLACEHOLDER] Replacements:', replacements);
  //
  //   let replacedQuestion = question;
  //
  //   Object.entries(replacements).forEach(([placeholder, value]) => {
  //     const regex = new RegExp(`<${placeholder}>`, 'g');
  //     console.log(`[PLACEHOLDER] Replacing <${placeholder}> with: "${value}"`);
  //     replacedQuestion = replacedQuestion.replace(regex, value);
  //   });
  //
  //   console.log('[PLACEHOLDER] Original:', question, 'Replaced:', replacedQuestion);
  //   return replacedQuestion;
  // }

  // // Helper method to get license requirement data from session storage
  // private getLicenseRequirementsData(): any[] {
  //   const reqDataStr = this.sessionStorage.getItem('license-requirements-data');
  //   if (!reqDataStr) {
  //     console.log('[LICENSE DATA] No license requirements data found in session storage');
  //     return [];
  //   }
  //
  //   try {
  //     const data = JSON.parse(reqDataStr);
  //     console.log('[LICENSE DATA] Parsed requirements data:', data);
  //     return Array.isArray(data) ? data : [];
  //   } catch (error) {
  //     console.error('[LICENSE DATA] Failed to parse requirements data:', error);
  //     return [];
  //   }
  // }

  // // Helper method to get incomplete license requirements with due date <= license expiry
  // private getQualifyingRequirements(): Array<{ requirement: string; dueDate: string; status: string }> {
  //   console.log('[QUALIFYING REQS] Getting qualifying requirements...');
  //   const requirements = this.getLicenseRequirementsData();
  //   console.log('[QUALIFYING REQS] License requirements from session:', requirements);
  //
  //   // Get license expiry date from dashboard data (same as checkRenewConditions and onQualifyingContinue)
  //   const licenseData = this.dashboardData?.Obj_License?.[0];
  //   console.log('[QUALIFYING REQS] License data from dashboard:', licenseData);
  //   if (!licenseData || !licenseData.dtExpiryDate) {
  //     console.log('[QUALIFYING REQS] License expiry date not found in dashboard data');
  //     return [];
  //   }
  //
  //   // Parse license expiry date from API response
  //   const expiryDate = new Date(licenseData.dtExpiryDate);
  //   console.log('[QUALIFYING REQS] License expiry date:', expiryDate);
  //
  //   // Filter requirements:
  //   // 1. If status = 'incomplete' → Include (show the question)
  //   // 2. If status = null → Check if due_date <= license expiry
  //   // 3. If status = any other value → Exclude
  //   const qualifying = requirements.filter((req: any) => {
  //     console.log('[QUALIFYING REQS] Processing requirement:', req);
  //     // If status exists and is 'incomplete', include it directly
  //     if (req.status && req.status.toLowerCase() === 'pending') {
  //       console.log('[QUALIFYING REQS] Status is incomplete - including requirement:', req.requirement);
  //       return true;
  //     }
  //
  //     // If status is null, check the due date
  //     if (!req.status) {
  //       if (!req.due_date) {
  //         console.log('[QUALIFYING REQS] Requirement missing due date');
  //         return false;
  //       }
  //
  //       const reqParts = req.due_date.split('/');
  //       const reqDate = new Date(parseInt(reqParts[2]), parseInt(reqParts[0]) - 1, parseInt(reqParts[1]));
  //
  //       const isQualifying = reqDate <= expiryDate;
  //       console.log('[QUALIFYING REQS] Status null - Requirement:', req.requirement, 'DueDate:', req.due_date, 'Qualifies:', isQualifying);
  //       return isQualifying;
  //     }
  //
  //     // Status has some other value - exclude it
  //     console.log('[QUALIFYING REQS] Excluding requirement with status:', req.status);
  //     return false;
  //   });
  //
  //   console.log('[QUALIFYING REQS] Total qualifying requirements found:', qualifying.length);
  //   console.log('[QUALIFYING REQS] Qualifying requirements:', qualifying);
  //   const results = qualifying.map(req => ({
  //     requirement: req.requirement,
  //     dueDate: req.due_date,
  //     status: req.status
  //   }));
  //   console.log('[QUALIFYING REQS] Final mapped results:', results);
  //   return results;
  // }

  // // Helper method to get license provision data from session storage
  // private getQualifyingProvisions(): Array<{ provision: string; dueDate: string; status: string }> {
  //   console.log('[QUALIFYING PROV] Getting qualifying provisions...');
  //   const provDataStr = this.sessionStorage.getItem('license-provisions-data');
  //
  //   if (!provDataStr) {
  //     console.log('[QUALIFYING PROV] Missing provision data');
  //     return [];
  //   }
  //
  //   try {
  //     const provisions = JSON.parse(provDataStr);
  //     console.log('[QUALIFYING PROV] License provisions from session:', provisions);
  //
  //     // Get license expiry date from dashboard data (same as checkRenewConditions and onQualifyingContinue)
  //     const licenseData = this.dashboardData?.Obj_License?.[0];
  //     console.log('[QUALIFYING PROV] License data from dashboard:', licenseData);
  //     if (!licenseData || !licenseData.dtExpiryDate) {
  //       console.log('[QUALIFYING PROV] License expiry date not found in dashboard data');
  //       return [];
  //     }
  //
  //     // Parse license expiry date from API response
  //     const expiryDate = new Date(licenseData.dtExpiryDate);
  //     console.log('[QUALIFYING PROV] License expiry date:', expiryDate);
  //
  //     // Filter provisions:
  //     // 1. If status = 'incomplete' → Include (show the question)
  //     // 2. If status = null → Check if due_date <= license expiry
  //     // 3. If status = any other value → Exclude
  //     const qualifying = provisions.filter((prov: any) => {
  //       console.log('[QUALIFYING PROV] Processing provision:', prov);
  //       // If status exists and is 'incomplete', include it directly
  //       if (prov.status && prov.status.toLowerCase() === 'pending') {
  //         console.log('[QUALIFYING PROV] Status is incomplete - including provision:', prov.provision);
  //         return true;
  //       }
  //
  //       // If status is null, check the due date
  //       if (!prov.status) {
  //         if (!prov.due_date) {
  //           console.log('[QUALIFYING PROV] Provision missing due date');
  //           return false;
  //         }
  //
  //         const provParts = prov.due_date.split('/');
  //         const provDate = new Date(parseInt(provParts[2]), parseInt(provParts[0]) - 1, parseInt(provParts[1]));
  //
  //         const isQualifying = provDate <= expiryDate;
  //         console.log('[QUALIFYING PROV] Status null - Provision:', prov.provision, 'DueDate:', prov.due_date, 'Qualifies:', isQualifying);
  //         return isQualifying;
  //       }
  //
  //       // Status has some other value - exclude it
  //       console.log('[QUALIFYING PROV] Excluding provision with status:', prov.status);
  //       return false;
  //     });
  //
  //     console.log('[QUALIFYING PROV] Total qualifying provisions found:', qualifying.length);
  //     console.log('[QUALIFYING PROV] Qualifying provisions:', qualifying);
  //     const results = qualifying.map(prov => ({
  //       provision: prov.provision_details,
  //       dueDate: prov.due_date,
  //       status: prov.status
  //     }));
  //     console.log('[QUALIFYING PROV] Final mapped results:', results);
  //     return results;
  //   } catch (error) {
  //     console.error('[QUALIFYING PROV] Failed to parse provisions data:', error);
  //     return [];
  //   }
  // }

  // // Helper method to check if should show professional development questions
  // private shouldShowPDQuestions(): boolean {
  //   console.log('[PD QUESTIONS] Checking if should show PD questions...');
  //   const appType = this.currentApplicationType;
  //   const licenseType = this.sessionStorage.getItem('licenseType');
  //
  //   console.log('[PD QUESTIONS] App type:', appType);
  //   console.log('[PD QUESTIONS] License type from session:', licenseType);
  //
  //   // Show only for Reg-Ini licensees with Standard or Professional license type
  //   if (appType !== 'Regular - Initial') {
  //     console.log('[PD QUESTIONS] Not showing - not Regular-Initial');
  //     return false;
  //   }
  //
  //   if (!licenseType || (licenseType !== 'Standard' && licenseType !== 'Professional')) {
  //     console.log('[PD QUESTIONS] Not showing - invalid license type:', licenseType);
  //     return false;
  //   }
  //
  //   console.log('[PD QUESTIONS] Should show - Regular-Initial with', licenseType, 'license');
  //   return true;
  // }


  // Method to process questions - apply conditional logic and placeholder replacement
  private processQuestionsWithConditionalLogic(): void {
    console.log('[PROCESS QUESTIONS] Starting question processing');
    console.log('[PROCESS QUESTIONS] Original questions:', this.qualifyingQuestions);
    console.log('[PROCESS QUESTIONS] Application type:', this.currentApplicationType);
    console.log('[PROCESS QUESTIONS] Dashboard data:', this.dashboardData);

    // Clear the license requirement and provision key tracking sets
    this.licenseRequirementKeys.clear();
    this.licenseProvisionKeys.clear();

    const processedQuestions: string[] = [];
    const processedKeys: string[] = [];
    const processedIndices: number[] = [];

    for (let i = 0; i < this.qualifyingQuestions.length; i++) {
      const question = this.qualifyingQuestions[i];
      const key = this.qualifyingQuestionKeys[i];
      let shouldInclude = true;
      let processedQuestion = question;

      console.log(`[PROCESS] Processing question ${i}: "${question}"`);
      console.log(`[PROCESS] Question key: ${key}`);

      // ====================================================================================
      // COMMENTED OUT: Dynamic expansion logic for License Requirements, License Provisions, and Professional Development
      // Now using static questions from API instead
      // ====================================================================================

      // // Check question type and apply conditional logic
      // if (question.includes('<License_Requirement>')) {
      //   console.log('[PROCESS] Found License_Requirement placeholder');
      //   // License Requirements question
      //   const requirements = this.getQualifyingRequirements();
      //   console.log('[PROCESS] License requirement question - qualifying requirements:', requirements.length);
      //   console.log('[PROCESS] Requirements data:', requirements);
      //
      //   if (requirements.length === 0) {
      //     shouldInclude = false;
      //     console.log('[PROCESS] No qualifying requirements - hiding question');
      //   } else {
      //     // Track this key as a license requirement key
      //     this.licenseRequirementKeys.add(key);
      //     // Expand this question for each requirement
      //     console.log("requirement data :", requirements)
      //     requirements.forEach((req, idx) => {
      //       const expanded = this.replacePlaceholders(question, {
      //         'License_Requirement': req.requirement,
      //         'License_Requirement_Due _Date': req.dueDate
      //       });
      //       processedQuestions.push(expanded);
      //       processedKeys.push(key);
      //       processedIndices.push(i);
      //       console.log(`[PROCESS] Added requirement ${idx + 1}:`, expanded);
      //     });
      //     shouldInclude = false; // Skip original, we've added expanded versions
      //   }
      // } else if (question.includes('<License_Provision>')) {
      //   console.log('[PROCESS] Found License_Provision placeholder');
      //   // License Provisions question
      //   const provisions = this.getQualifyingProvisions();
      //   console.log('[PROCESS] License provision question - qualifying provisions:', provisions.length);
      //   console.log('[PROCESS] Provisions data:', provisions);
      //
      //   if (provisions.length === 0) {
      //     shouldInclude = false;
      //     console.log('[PROCESS] No qualifying provisions - hiding question');
      //   } else {
      //     // Track this key as a license provision key
      //     this.licenseProvisionKeys.add(key);
      //     // Expand this question for each provision
      //     provisions.forEach((prov, idx) => {
      //       const expanded = this.replacePlaceholders(question, {
      //         'License_Provision': prov.provision,
      //         'License_Provision_Due _Date': prov.dueDate
      //       });
      //       processedQuestions.push(expanded);
      //       processedKeys.push(key);
      //       processedIndices.push(i);
      //       console.log(`[PROCESS] Added provision ${idx + 1}:`, expanded);
      //     });
      //     shouldInclude = false; // Skip original
      //   }
      // } else if (question.includes('<Professional_Development>')) {
      //   console.log('[PROCESS] Found Professional_Development placeholder');
      //   // Professional Development question
      //   if (!this.shouldShowPDQuestions()) {
      //     shouldInclude = false;
      //     console.log('[PROCESS] Professional development - hiding (not Reg-Ini or invalid license type)');
      //   } else {
      //     const expanded = this.replacePlaceholders(question, {
      //       'Professional_Development': 'Professional Development'
      //     });
      //     processedQuestion = expanded;
      //     console.log('[PROCESS] Professional development - showing');
      //   }
      // }
      // // Add non-placeholder questions as-is
      // else {
      //   console.log('[PROCESS] Generic question - including as-is');
      // }

      // ====================================================================================
      // Now using static questions from API - include all questions as-is
      // ====================================================================================
      console.log('[PROCESS] Using static questions - including as-is');

      console.log(`[PROCESS] Question ${i} shouldInclude: ${shouldInclude}`);
      if (shouldInclude) {
        processedQuestions.push(processedQuestion);
        processedKeys.push(key);
        processedIndices.push(i);
        console.log(`[PROCESS] Added to final list: "${processedQuestion}"`);
      }
    }

    // // Update questions
    // this.qualifyingQuestions = processedQuestions;
    // this.qualifyingQuestionKeys = processedKeys;
    // this.visibleQuestionIndices = processedIndices;

    // console.log('[PROCESS QUESTIONS] Processing complete');
    // console.log('[PROCESS QUESTIONS] Final questions count:', this.qualifyingQuestions.length);
    // console.log('[PROCESS QUESTIONS] Final questions:', this.qualifyingQuestions);
        // Deduplicate questions by text content (keep first occurrence)
    // Prevents same question appearing when a record exists in both
    // license-requirements-data and license-provisions-data session storage
    const seen = new Set<string>();
    const dedupedQuestions: string[] = [];
    const dedupedKeys: string[] = [];
    const dedupedIndices: number[] = [];

    for (let j = 0; j < processedQuestions.length; j++) {
      if (!seen.has(processedQuestions[j])) {
        seen.add(processedQuestions[j]);
        dedupedQuestions.push(processedQuestions[j]);
        dedupedKeys.push(processedKeys[j]);
        dedupedIndices.push(processedIndices[j]);
      } else {
        console.log('[PROCESS QUESTIONS] Removing duplicate question:', processedQuestions[j]);
      }
    }

    // Update questions
    this.qualifyingQuestions = dedupedQuestions;
    this.qualifyingQuestionKeys = dedupedKeys;
    this.visibleQuestionIndices = dedupedIndices;

    console.log('[PROCESS QUESTIONS] Processing complete');
    console.log('[PROCESS QUESTIONS] Final questions count:', this.qualifyingQuestions.length);
    console.log('[PROCESS QUESTIONS] Final questions:', this.qualifyingQuestions);

  }

  // Helper method to find key by value in lookup data
  private findKeyByValue(dataArray: any[], targetValue: string): any {
    const foundItem = dataArray.find(item => item.value === targetValue);
    return foundItem ? foundItem.key : null;
  }

  // Helper method to find value by key in lookup data
  private findValueByKey(dataArray: any[], targetKey: string): string {
    const foundItem = dataArray.find(item => item.key === targetKey);
    return foundItem ? foundItem.value : targetKey;
  }

  // Helper method to find suffix value by key
  private getSuffixByKey(suffixKey: any): string {
    if (!suffixKey || !this.suffixData) return '';
    const foundItem = this.suffixData.find(item => item.key === suffixKey);
    this.personData.suffixValue = foundItem.value; // Store suffix in personData for later use
    this.sessionStorage.setObj('personData', this.personData); // Update session storage with suffix
    return foundItem ? foundItem.value : '';
  }

  // Helper method to map application type keys to display values
  private getApplicationTypeDisplayValue(key: string): string {
    if (!key || key === '' || key === null || key === undefined) {
      return '';
    }

    // Hardcoded mapping for application types not in modal (key 7, 8, 9, and 12)
    const hardcodedMapping: { [key: string]: string } = {
      '7': 'License Extension',
      '8': 'ARL + Substitute',
      '9': 'Substitute + ARL',
      '10': 'Surrender',
      '12': 'Add Endorsement'
    };

    // Check hardcoded mapping first
    if (hardcodedMapping[key]) {
      return hardcodedMapping[key];
    }

    // Otherwise, look up in applicationTypes array
    if (this.applicationTypes) {
      const appType = this.applicationTypes.find(type => type.key === key);
      return appType ? appType.value : '';
    }

    return '';
  }

  // Helper method to convert text to proper case (capitalize first letter of each word)
  private toProperCase(text: string): string {
    if (!text) return '';
    return text.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  private stripTime(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  selectType(type: string) {
  this.selectedType = type;
  this.errorMessage = '';
  }

  onCancel() {
    this.dialogRef.close();
  }

  onBack() {
    // Reset qualifying questions state and go back to application type selection
    this.showQualifyingQuestions = false;
    this.qualifyingQuestions = [];
    this.qualifyingAnswers = [];
    this.qualifyingErrors = [];
    this.qualifyingGlobalError = null;
    this.qualifyingQuestionKeys = [];
    this.visibleQuestionIndices = [];
    this.currentApplicationType = '';
    this.oldApplicationId = null;
    this.oldApplicationIntegerId = null;
    this.mostRecentActiveAppId = null;
    this.licensePath = null;
    this.displayApplicationType = '';
    this.licenseRequirementKeys.clear();
    this.licenseProvisionKeys.clear();
    this.isCreatingApplication = false;

    // Reset ARL program fields
    this.showArlProgramFields = false;
    this.arlProviderName = null;
    this.arlProgramArea = null;
    this.arlCompletionDate = null;
    this.arlProgramAreaError = '';
    this.arlCompletionDateError = '';
    this.arlProgramAreaTouched = false;
    this.arlCompletionDateTouched = false;

    console.log('[BACK] Navigated back to application type selection');
  }

  async onContinue() {
    if (this.isCreatingApplication) {
      console.log('[ON CONTINUE] Application creation already in progress, ignoring duplicate click');
      return;
    }
    this.isCreatingApplication = true;
    if (!this.selectedType) {
      this.isCreatingApplication = false;
      this.errorMessage = 'Please select an application type.';
      return;
    } else {
      this.errorMessage = '';
    }


    if (this.selectedType === 'surrender') {
      console.log('[ON CONTINUE] Handling Surrender action');
      this.dialogRef.close({ action: 'surrender' });
      return;
    }
    if (this.selectedType === 'extend') {
      console.log('[ON CONTINUE] Handling Extend action');
      this.dialogRef.close({ action: 'extend' });
      return;
    }
    if (this.selectedType === 'add-endorsement') {
      console.log('[ON CONTINUE] Handling Add Endorsement action');
      this.dialogRef.close({ action: 'add-endorsement' });
      return;
    }
    if (this.selectedType === 'resolve-provisions') {
      console.log('[ON CONTINUE] Handling Resolve Provisions action');
      this.dialogRef.close({ action: 'resolve-provisions' });
      return;
    }
    this.selectTypeValue = this.applicationTypes.filter(ele => ele.key === this.selectedType);
    if (this.selectTypeValue[0].value == 'Name Change')
      this.selectTypeValue[0].value = 'Name Change';
    else
      console.log("Selected changes : ", this.selectTypeValue[0].value);


    // Call RCR Questions API if 'Renew, Convert or Re-Apply' is selected
    if (this.selectedType === 'renew-convert-reapply') {

      // Use the application ID from the valid RCR application found in checkRenewConditions
      if (!this.validRcrApplicationId) {
        console.warn('[RCR API] No valid RCR application ID found from checkRenewConditions');
        this.isCreatingApplication = false;
        return;
      }

      // Store the valid RCR application ID as oldApplicationId for later use
      this.oldApplicationId = this.validRcrApplicationId;
      console.log('[RCR API] Using validRcrApplicationId:', this.validRcrApplicationId);
      console.log('[RCR API] Set oldApplicationId to:', this.oldApplicationId);

      // Find the integer application_id from applicationsData using application_number
      const matchingApp = this.applicationsData.find((app: any) =>
        app.application_number === this.oldApplicationId
      );

      if (matchingApp && matchingApp.id) {
        this.oldApplicationIntegerId = matchingApp.id;
        console.log('[RCR API] Found integer application_id:', this.oldApplicationIntegerId, 'for application_number:', this.oldApplicationId);
      } else {
        console.warn('[RCR API] Could not find integer application_id for application_number:', this.oldApplicationId);
      }

      // Get most recent approved application type from dashboard-data response
      let applicationType = '';
      if (this.dashboardData && Array.isArray(this.dashboardData.li_Application) && this.dashboardData.li_Application.length > 0) {
        // Filter approved applications
        const approvedApps = this.dashboardData.li_Application.filter((app: any) =>
          app.strAppStatus?.includes('Approved')
        );

        if (approvedApps.length > 0) {
          // Sort by date created to get most recent (descending order)
          approvedApps.sort((a: any, b: any) => {
            const dateA = new Date(a.dtDateCreated).getTime();
            const dateB = new Date(b.dtDateCreated).getTime();
            return dateB - dateA; // Most recent first
          });

          // Find the first approved application that is NOT 'License Extension' or 'Name Change'
          let validApp: any = null;
          for (const app of approvedApps) {
            const appType = app.strAppType;
            console.log('[RCR API] Checking application:', {
              appId: app.strAppId,
              appType: appType,
              createdDate: app.dtDateCreated
            });

            // Skip 'License Extension', 'Name Change', 'Add Endorsement', and 'Surrender'
            if (appType === 'License Extension' || appType === 'Name Change' || appType === 'Add Endorsement' || appType === 'Surrender') {
              console.log('[RCR API] Skipping application - type is License Extension, Name Change, Add Endorsement, or Surrender');
              continue;
            }

            // Found a valid application type
            validApp = app;
            console.log('[RCR API] Found valid application for RCR:', {
              appId: app.strAppId,
              appType: appType
            });
            break;
          }

          // If all approved applications are License Extension, Name Change, Add Endorsement, or Surrender, exit
          if (!validApp) {
            console.warn('[RCR API] FAILED: All approved applications are License Extension, Name Change, Add Endorsement, or Surrender');
            this.isCreatingApplication = false;
            return;
          }

          applicationType = validApp.strAppType;
          console.log('[RCR API] Most recent approved application type:', applicationType);
        } else {
          console.warn('[RCR API] No approved applications found in dashboard-data');
          this.isCreatingApplication = false;
          return; // Exit early if no approved applications
        }
      } else {
        console.warn('[RCR API] No application data found in dashboard-data response.');
        this.isCreatingApplication = false;
        return; // Exit early if no application data
      }
      try {
        console.log('[RCR API] Requesting questions for application type:', applicationType);
        // const response: any = await this.httpService.get(`${API_ENDPOINTS.RCR_QUESTIONS}?application_type=${applicationType}`).toPromise();
        console.log('[RCR API] Full approved apps:', this.dashboardData.li_Application.filter((app: any) => app.strAppStatus?.includes('Approved')));

        // const apiUrl = `${API_ENDPOINTS.RCR_QUESTIONS}?application_type=${encodeURIComponent(applicationType)}`;
        // Map 'Substitute + ARL' to use same questions as ARL
        let apiApplicationType = applicationType;
        if (applicationType === 'Substitute + ARL') {
          apiApplicationType = 'ARL (Alternative Route to Licensure)';
          console.log('[RCR API] Mapping Substitute + ARL to ARL for questions API');
        }

        const apiUrl = `${API_ENDPOINTS.RCR_QUESTIONS}?application_type=${encodeURIComponent(apiApplicationType)}`;
        console.log('[RCR API] Constructed API URL:', apiUrl);

        const response: any = await this.httpService.get(apiUrl).toPromise();
        console.log('[RCR API] Response:', response);
        console.log('[RCR API] Response validation:', {
          hasStatus: !!response?.status,
          statusValue: response?.status,
          hasData: !!response?.data?.data,
          isDataArray: Array.isArray(response?.data?.data),
          dataLength: response?.data?.data?.length
        });

        if (response?.status === 'success' && response?.data?.data && Array.isArray(response.data.data)) {
          this.currentApplicationType = applicationType; // Store for later use

          // Check if response contains objects or strings
          const firstItem = response.data.data[0];
          if (typeof firstItem === 'string') {
            // Response is array of strings (Regular - Initial, ARL, Apprentice, Substitute, Emergency Substitute)
            this.qualifyingQuestions = response.data.data;
            // For Substitute, assign keys: '1', '2', '5' (matches database keys)
            // For Emergency Substitute, assign keys: '1', '2', '4' (matches database keys)
            // For other types, keys don't matter (not used)
            if (this.currentApplicationType === 'Substitute') {
              this.qualifyingQuestionKeys = ['1', '2', '5']; // Hardcoded to match database
              console.log('[SUBSTITUTE] Assigned question keys:', this.qualifyingQuestionKeys);
            } else if (this.currentApplicationType === 'Emergency Substitute') {
              this.qualifyingQuestionKeys = ['1', '2', '4']; // Hardcoded to match database
              console.log('[EMERGENCY SUBSTITUTE] Assigned question keys:', this.qualifyingQuestionKeys);
            } else {
              this.qualifyingQuestionKeys = response.data.data.map((_: any, index: number) => index.toString());
            }
          } else if (typeof firstItem === 'object' && firstItem !== null) {
            // New format: array of objects with key and value (Substitute)
            this.qualifyingQuestions = response.data.data.map((q: any) => q.value);
            this.qualifyingQuestionKeys = response.data.data.map((q: any) => q.key);
          } else {
            console.warn('[RCR API] Unknown response format:', response.data.data);
            this.isCreatingApplication = false;
            return;
          }
          // Process questions with conditional logic and placeholder replacement
          this.processQuestionsWithConditionalLogic();

          this.qualifyingAnswers = new Array(this.qualifyingQuestions.length).fill(null);
          this.qualifyingErrors = new Array(this.qualifyingQuestions.length).fill('');
          this.visibleQuestionIndices = this.qualifyingQuestions.map((_: any, index: number) => index); // All questions visible initially

          // Apply conditional logic for License Provisions question
          this.filterLicenseProvisionsQuestion();

          this.qualifyingGlobalError = null;
          this.showQualifyingQuestions = true;
          console.log('[RCR API] Questions loaded:', this.qualifyingQuestions.length);
          console.log('[RCR API] Questions:', this.qualifyingQuestions);
          console.log('[RCR API] Question keys:', this.qualifyingQuestionKeys);
          console.log('[RCR API] Question keys with indices:', this.qualifyingQuestionKeys.map((key, idx) => `[${idx}] = '${key}'`));
          console.log('[RCR API] Visible question indices after filtering:', this.visibleQuestionIndices);
          console.log('[RCR API] showQualifyingQuestions set to:', this.showQualifyingQuestions);
          this.isCreatingApplication = false;
          return; // Don't proceed further, show questions first
        } else {
          console.warn('[RCR API] Invalid response structure:', response);
          this.isCreatingApplication = false;
          return; // Exit early if invalid response
        }
      } catch (error) {
        console.error('[RCR API] Error calling RCR Questions API:', error);
        this.isCreatingApplication = false;
        return; // Exit early on error
      }
    }

    // For normal flow (non-RCR), set Initial license path for all application types except Name Change
    if (this.selectedType !== '5') { // Exclude Name Change (key=5)
      this.licensePath = '3'; // Initial
      this.sessionStorage.setItem('license-path', '3');
      console.log('[NORMAL FLOW] Set license_path = 3 (Initial) for application type:', this.selectedType);
    } else {
      console.log('[NORMAL FLOW] Name Change application - no license_path set');
    }

    try {
      this.isCreatingApplication = false;
      await this.getPerson();
      this.dialogRef.close(this.selectedType);
    } catch (error) {
      this.isCreatingApplication = false;
      console.error('Error in onContinue flow:', error);
      // Handle error appropriately
    }
  }
  getPerson(): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = JSON.parse(this.sessionStorage.getItem('user') || '{}');
      this.appService.getPerson(user?.id).subscribe({
        next: (data) => {
          this.personData = data.data[0];
          this.getSuffixByKey(this.personData.cd_suffix); // This will also store suffix value in personData and session storage
          console.log(data.data[0]);
        this.sessionStorage.setObj('personData', this.personData);
          this.createApplication().then(resolve).catch(reject);
        },
        error: (error) => {
          console.error('Error getting person data:', error);
          reject(error);
        }
      });
    });
  }
  getAppData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.appService.getAppData(`personId=${this.personData.person_id}`).subscribe({
        next: (data) => {

          this.navigationData = data;
          console.log("App Data---->", data.data.data[0]);
          const { activeversionid } = data.data.data[0];

          // Navigate to the wizard
          this.router.navigate([`/pages/dynamic-routing/tab/${activeversionid}`]);

          resolve();

        },
        error: (error) => {
          console.error('Error getting app data:', error);
          reject(error);
        }
      });
    });
  }
 getFormatedDate(created_date : any) {
    const currDate = new Date(created_date);
      const month = (currDate.getMonth() + 1).toString().padStart(2, '0');
      const day = currDate.getDate().toString().padStart(2, '0');
      const year = currDate.getFullYear();
      const formattedDate = `${month}/${day}/${year}`;
      return formattedDate;
  }
  createApplication(): Promise<void> {
    return new Promise((resolve, reject) => {

      if (this.isCreatingApplication) {
        console.log('[CREATE APP] Application creation already in progress, ignoring duplicate click');
        reject(new Error('Application creation already in progress'));
        return;
      }

      this.isCreatingApplication = true;
      console.log('[CREATE APP] Setting isCreatingApplication flag to true');
      // Always set initial status to 'In Progress, Not Submitted' for all application types
      // including Name Change in license flow
      const draftStatusKey = this.findKeyByValue(this.applicationStatusData, 'In Progress, Not Submitted');
      let request: any = {
        cd_application_status: draftStatusKey,
        cd_application_type: this.selectedType,
        person_id: this.personData.person_id,
        created_by: JSON.parse(this.sessionStorage.getItem("id"))
      };

      // Add license_path for both RCR and normal flow applications
      if (this.licensePath) {
        request.license_path = this.licensePath;
        console.log('[CREATE APP] Adding license_path to request:', this.licensePath);
      }

      // Add old_id, and copyFromPersonWise only for RCR/Renew flow
      console.log('[CREATE APP] Checking RCR values - oldApplicationId:', this.oldApplicationId, 'licensePath:', this.licensePath);
      console.log('[CREATE APP] Types - oldApplicationId:', typeof this.oldApplicationId, 'licensePath:', typeof this.licensePath);
      if (this.oldApplicationId && this.licensePath) {
        // Send the most recent active application's integer id as old_id (the actual clone source).
        // Fall back to oldApplicationId (application_number string) if not available.
        request.old_id = this.mostRecentActiveAppId ?? this.oldApplicationId;
        request.copyFromPersonWise = true; // Only for RCR flow - triggers data cloning
        console.log('[CREATE APP] RCR flow - Adding old_id:', request.old_id, '(mostRecentActiveAppId:', this.mostRecentActiveAppId, ') copyFromPersonWise: true');
        console.log('[CREATE APP] Request object after adding fields:', request);
        console.log('[CREATE APP] request.license_path value:', request.license_path, 'type:', typeof request.license_path);
      } else {
        console.log('[CREATE APP] Non-RCR flow - no old_id or copyFromPersonWise');
        console.log('[CREATE APP] Condition check - oldApplicationId truthy?', !!this.oldApplicationId, 'licensePath truthy?', !!this.licensePath);
      }

      console.log('[CREATE APP] Final request payload (stringified):', JSON.stringify(request, null, 2));
      console.log('[CREATE APP] Final request payload (direct object):', request);
      this.appService.createApplication(request).subscribe({
        next: response => {
          this.isCreatingApplication = false;
            if(response.data.isPerson === false){
           this.noPersonData = true;
           this.dialogRef.close();
           alert("This person doesn't exist");
          }else{
          console.log('Application created successfully:', response);

          // Store application data from create response
          const appTypeValue = this.selectTypeValue[0].value === 'Regular - Initial' ? 'Regular-initial' : this.selectTypeValue[0].value;
            this.sessionStorage.setItem('applicationType', JSON.stringify(this.selectTypeValue[0].value));
            this.sessionStorage.setItem('youthID',JSON.stringify(response.data.data.application_id));
            this.sessionStorage.setItem('youthNumber', JSON.stringify(response.data.data.application_number));
            this.sessionStorage.setItem('applicationID', JSON.stringify(response.data.data.application_id))
            this.sessionStorage.setItem('gridAction', null);
            this.dataStoreService.setData('gridAction','');
          this.sessionStorage.setItem('currApplicationType', JSON.stringify(this.selectTypeValue[0].value));
          this.sessionStorage.setItem('applicationStatus', JSON.stringify('In Progress, Not Submitted'));;
          let inputFormatForAppData = { };
          // Determine correct application type for banner
          let applicationTypeForBanner = this.selectTypeValue[0].value;
          const licenseApplicationTypeKey = this.sessionStorage.getItem('license-application-type');
          if (licenseApplicationTypeKey && licenseApplicationTypeKey !== 'null') {
            const mappedApplicationType = this.getApplicationTypeDisplayValue(licenseApplicationTypeKey);
            if (mappedApplicationType) {
              applicationTypeForBanner = mappedApplicationType;
            }
          }

          // Determine License Path label before creating banner header
          let licensePathLabel = '';
          const licensePathValue = this.sessionStorage.getItem('license-path');
          if (licensePathValue && licensePathValue !== 'null') {
            licensePathLabel = this.getLicensePathLabel(licensePathValue);
          }

          const bannerheader = {
              "Application ID": response.data.data.application_number,
              "Application Type": applicationTypeForBanner,
              ...(licensePathLabel && { "License Path": licensePathLabel }),
              "Educator Name": `${this.toProperCase(this.personData.first_name)} ${this.toProperCase(this.personData.last_name)}`.trim(),
              "Created Date": this.getFormatedDate(response.data.data.created_date),
          };
          const licenseNum = this.dashboardData?.Obj_License?.[0]?.strLicenseNumber;
          if (licenseNum) {
            bannerheader['License Number'] = licenseNum;
          }
          // Set cd_license_type in session storage from dashboard data.
          // Prefer Standard/Professional license type when person has multiple active licenses.
          const objLicenseList: any[] = Array.isArray(this.dashboardData?.Obj_License)
            ? this.dashboardData.Obj_License
            : (this.dashboardData?.Obj_License ? [this.dashboardData.Obj_License] : []);
          const preferredLicenseRow = objLicenseList.find((row: any) => {
            const t = String(row?.strLicenseType || '').trim().toLowerCase();
            return t === 'standard' || t === 'professional';
          });
          const cdLicenseType = preferredLicenseRow?.strLicenseType || objLicenseList[0]?.strLicenseType;
          if (cdLicenseType) {
            this.sessionStorage.setItem('cd_license_type', cdLicenseType);
            console.log('[RCR] Stored cd_license_type in session:', cdLicenseType);
          }
          // if (response.data.data.cd_application_status) {
          //      bannerheader['Application Status'] = this.applicationStatusData.find(item => item.key === response.data.data.cd_application_status)?.value;
          // }
          if (response.data.data.re_submitted_date && response.data.data.re_submitted_date != '---') {
               bannerheader['Resubmitted Date'] = this.getFormatedDate(response.data.data.re_submitted_date);
          }
          inputFormatForAppData = {
            "bannerheader": bannerheader
          };
          // if(response.data.data.cd_application_status) {
          //   const statusValue = this.applicationStatusData.find(item => item.key === response.data.data.cd_application_status)?.value;
          //    inputFormatForAppData = {
          //   "bannerheader": {
          //     "Application ID": response.data.data.application_number,
          //     "Application Type": this.selectTypeValue[0].value,
          //     "Educator Name": `${this.toProperCase(this.personData.first_name)} ${this.toProperCase(this.personData.middle_name)} ${this.toProperCase(this.personData.last_name)} ${this.getSuffixByKey(this.personData.cd_suffix)}`.trim(),
          //     "Date Created": this.getFormatedDate(response.data.data.created_date),
          //     "Application Status": statusValue
          //   }
          //   };
          // }else{
          //   inputFormatForAppData = {
          //   "bannerheader": {
          //     "Application ID": response.data.data.application_number,
          //     "Application Type": this.selectTypeValue[0].value,
          //     "Educator Name": `${this.toProperCase(this.personData.first_name)} ${this.toProperCase(this.personData.middle_name)} ${this.toProperCase(this.personData.last_name)} ${this.getSuffixByKey(this.personData.cd_suffix)}`.trim(),
          //     "Date Created": this.getFormatedDate(response.data.data.created_date)
          //   }
          //   };
          // }
          // Create navigation data with application info from response
          this.sessionStorage.setObj('navigationData',inputFormatForAppData);
          this.sessionStorage.setObj('backToHome',  '/pages/my-app');
          this.sessionStorage.setItem('backButtonText', 'Close');
          this.sessionStorage.setItem('backToGridPage', JSON.stringify({ name: '/my-app' }));
          this.sessionStorage.setItem('isFirstSave', 'false');
          this.fetchAndSetApplicationFee(this.licensePath || '3');
          // After creating application, get the app data with the new application
          //this.createApplicationTimeline(response.data.data.application_id);
          if (!this.oldApplicationId || !this.licensePath) {
            this.createLicenseKind(response.data.data.application_id);
          }

          // Save ARL program fields if they are filled (save to NEW application)
          if (this.showArlProgramFields && response.data.data.application_id && (this.arlProviderName || this.arlProgramArea || this.arlCompletionDate)) {
            console.log('[ARL SAVE] Saving to NEW application ID:', response.data.data.application_id);
            this.saveArlProgramFields(response.data.data.application_id).catch(error => {
              console.error('Error saving ARL program fields:', error);
              // Don't reject the promise - continue with application creation
            });
          }

          this.getAppData().then(resolve).catch(reject);

        }
        error: error => {
          this.isCreatingApplication = false;
          console.error('Error creating application:', error);
          reject(error);
        }
      }
    });
    });
  }
createApplicationTimeline(application_id: any){
  // Set timeline status to 'In Progress, Not Submitted' for all application types
  // This applies to Name Change applications in license flow as well
  const draftStatusKey = this.findKeyByValue(this.applicationStatusData, 'In Progress, Not Submitted');
  const applicationTimelineData = {
    application_id: application_id,
    cd_application_status: draftStatusKey,
    person_id: this.personData.person_id,
  }
  // this.appService.createApplicationTimeline(applicationTimelineData).subscribe({
  //   next: response => {
  //     if (response.data.success === true) {
  //       console.log('Application timeline created successfully:', response);
  //     } else {
  //       console.warn('Application timeline creation returned false success status');
  //     }
  //   },
  //   error: error => {
  //     console.error('Error creating application timeline:', error);
  //   }
  // });
}

  /**
   * Save ARL program fields to database
   * @param application_id - The application ID to associate the ARL data with
   */
  saveArlProgramFields(application_id: any): Promise<void> {
    return new Promise((resolve, reject) => {
      // Find the provider by value (name) to get the key (ID) and traditional value from optionaldata
      const selectedProvider = this.arlProviderOptions.find(opt =>
        opt.value === this.arlProviderName && opt.optionaldata != null
      );
      const traditional = selectedProvider?.optionaldata?.Traditional ?? null;
      const providerKey = selectedProvider?.key ?? null; // Get the integer key/ID

      // Convert date string to ISO timestamp for PostgreSQL timestampz
      const completionTimestamp = this.arlCompletionDate
        ? new Date(this.arlCompletionDate).toISOString()
        : null;

      const requestPayload = {
        application_id: application_id,
        cd_arlprovider: providerKey ? parseInt(providerKey) : null,
        program_area: this.arlProgramArea,
        date_of_completion: completionTimestamp, // ISO timestamp for timestampz
        traditional: traditional,
        created_by: JSON.parse(this.sessionStorage.getItem("id"))
      };

      console.log('[ARL SAVE] Saving ARL program fields:', requestPayload);
      console.log('[ARL SAVE] Traditional value from optionaldata:', traditional);
      console.log('[ARL SAVE] ARL Provider Name:', this.arlProviderName);
      console.log('[ARL SAVE] ARL Provider Key (cd_arlprovider as integer):', providerKey);
      console.log('[ARL SAVE] Completion Date (converted to ISO timestamp):', completionTimestamp);
      this.httpService.post(API_ENDPOINTS.SAVE_ARL_PROGRAM_FIELDS, requestPayload).subscribe({
        next: (response: any) => {
          console.log('[ARL SAVE] ARL program fields saved successfully:', response);
          resolve();
        },
        error: (error: any) => {
          console.error('[ARL SAVE] Error saving ARL program fields:', error);
          this.alertService.error('Failed to save ARL program information. Please try again.');
          reject(error);
        }
      });
    });
  }

  /**
   * Load and pre-populate ARL program fields from database (for RCR flow)
   * @param application_id - The application ID to fetch ARL data from
   */
  loadArlProgramFields(application_id: any): void {
    console.log('[ARL LOAD] Fetching ARL program fields for application_id:', application_id);

    this.httpService.get(`${API_ENDPOINTS.GET_ARL_PROGRAM_FIELDS}?application_id=${application_id}`).subscribe({
      next: (response: any) => {
        console.log('[ARL LOAD] API response received');

        if (response?.data?.data) {
          const arlData = response.data.data;  // Access nested data object

          // Pre-populate Provider Name - handle both key (number) and value (string) from API
          if (arlData.cd_arlprovider) {
            const apiValue = arlData.cd_arlprovider;

            // Check if it's a number (key) or string (value)
            if (typeof apiValue === 'number' || !isNaN(Number(apiValue))) {
              // It's a KEY - find the matching provider value
              const providerKey = String(apiValue);
              const matchingProvider = this.arlProviderOptions.find(opt => opt.key === providerKey);

              if (matchingProvider) {
                this.arlProviderName = matchingProvider.value;
                console.log('[ARL LOAD]  Pre-populated Provider Name from KEY:', this.arlProviderName, '(key:', providerKey, ')');
              } else {
                console.warn('[ARL LOAD]  No matching provider found for key:', providerKey);
              }
            } else {
              // It's already a VALUE (provider name string) - use it directly
              this.arlProviderName = String(apiValue);
              console.log('[ARL LOAD]  Pre-populated Provider Name from VALUE:', this.arlProviderName);
            }

            // Force Angular change detection
            this.cdr.detectChanges();
          }
        } else {
          console.log('[ARL LOAD] No ARL program data found for this application');
        }
      },
      error: (error: any) => {
        console.error('[ARL LOAD] Error loading ARL program fields:', error);
        // Don't show error to user - they can fill fields manually
      }
    });
  }

  createLicenseKind(application_id: any) {
    // Determine license kind based on application type
    // Use explicit keys per request instead of looking up by value
    const appValue = this.selectTypeValue?.[0]?.value;
    let licenseKind: any = null;
    if (appValue === 'Substitute' || appValue === 'Emergency Substitute') {
      // Substitute / Emergency Substitute license kind key
      licenseKind = '10';
    } else if (appValue === 'Apprentice') {
      // Apprentice license kind key
      licenseKind = '7';
    }

    // Determine endorsement based on application type using explicit keys
    let endorsement: any = null;
    if (appValue === 'Substitute') {
      endorsement = '116';
    } else if (appValue === 'Emergency Substitute') {
      endorsement = '115';
    } else if (appValue === 'Apprentice') {
      endorsement = '92';
    }

    let licenseData = {
      application_id,
      cd_license_kind: licenseKind,
      cd_endorsement: endorsement
    }

    // Call API to save license data (you need to implement this method in your service)
    this.appService.licenseKind(licenseData).subscribe({
      next: response => {
        console.log('License kind created successfully:', response);
      },
      error: error => {
        console.error('Error creating license kind:', error);
      }
    });
  }

  /**
   * Loads RCR business rules from the backend API
   */
  private loadRcrBusinessRules(): void {
    let rulesLoadCompleted = 0;
    const totalRules = 2;

    const checkAllRulesLoaded = () => {
      rulesLoadCompleted++;
      if (rulesLoadCompleted === totalRules) {
        this.rcrBusinessRulesLoaded = true;
        console.log('[RCR BUSINESS RULES] All business rules loaded:', {
          rcrNonArlDays: this.rcrNonArlDays,
          rcrArlDays: this.rcrArlDays
        });

        // If dashboard data loading was waiting for business rules, check conditions now
        if (this.pendingRenewConditionCheck) {
          this.pendingRenewConditionCheck = false;
          this.checkRenewConditions();
          // checkRenewConditions can rewrite applicationTypes after the view was
          // already checked, so trigger change detection to render the updated list
          this.cdr.detectChanges();
          this.restoreConditionalOptions();
        }
      }
    };

    // Fetch RCR days for non-ARL applications
    this.registrationService.getBusinessRuleByKey('Application', 'rcr_non_arl_days')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.rcrNonArlDays = response.data.data.intValue;
            console.log('[RCR BUSINESS RULES] Loaded rcr_non_arl_days:', this.rcrNonArlDays);
          }
          checkAllRulesLoaded();
        },
        error: (error) => {
          console.error('Error fetching RCR non-ARL days rule:', error);
          checkAllRulesLoaded();
        }
      });

    // Fetch RCR days for ARL applications
    this.registrationService.getBusinessRuleByKey('Application', 'rcr_arl_days')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.rcrArlDays = response.data.data.intValue;
            console.log('[RCR BUSINESS RULES] Loaded rcr_arl_days:', this.rcrArlDays);
          }
          checkAllRulesLoaded();
        },
        error: (error) => {
          console.error('Error fetching RCR ARL days rule:', error);
          checkAllRulesLoaded();
        }
      });
  }

  /**
   * Determines if RCR flow should be shown based on application type and license expiration
   */
  private shouldShowRcrFlow(applicationType: string, licenseExpirationDate: Date): boolean {
    const currentDate = new Date();
    const expirationDate = new Date(licenseExpirationDate);

    // Calculate days difference
    const timeDiff = expirationDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    // Determine which business rule to use based on application type
    let allowedDays: number;

    if (applicationType === 'ARL (Alternative Route to Licensure)' || applicationType === 'Substitute + ARL') {
      allowedDays = this.rcrArlDays;
    } else if (['Regular - Initial', 'Substitute', 'Emergency Substitute', 'Apprentice', 'Add Endorsement', 'Surrender'].includes(applicationType)) {
      allowedDays = this.rcrNonArlDays;
    } else {
      console.log('[RCR DATE CHECK] Unknown application type:', applicationType);
      return false; // Unknown application type
    }

    console.log('[RCR DATE CHECK] Date validation:', {
      applicationType,
      daysDiff,
      allowedDays,
      currentDate: currentDate.toISOString().split('T')[0],
      expirationDate: expirationDate.toISOString().split('T')[0]
    });

    // Business rule logic:
    // - If license already expired (daysDiff < 0): Always allow RCR
    // - If license not yet expired (daysDiff >= 0): Only allow if within allowedDays from current date
    let shouldShow: boolean;
    if (daysDiff < 0) {
      // License already expired - always allow RCR
      shouldShow = true;
      console.log('[RCR DATE CHECK] License expired - always allowing RCR');
    } else {
      // License not yet expired - check if within allowed future range
      shouldShow = daysDiff <= allowedDays;
      console.log('[RCR DATE CHECK] License not expired - checking future range:', daysDiff, '<=', allowedDays, '=', shouldShow);
    }

    console.log('[RCR DATE CHECK] Should show RCR:', shouldShow);
    return shouldShow;
  }

  /**
   * Get License Path label from lookup data by key
   * @param licensePathKey The key to look up (e.g., '1', '2', '3', '4')
   * @returns The corresponding label from the lookup data
   */
  private getLicensePathLabel(licensePathKey: any): string {
    if (!licensePathKey) return '';
    const keyStr = String(licensePathKey);
    if (this.licensePathLookup?.length > 0) {
      const foundItem = this.licensePathLookup.find(item => String(item.key) === keyStr);
      return foundItem?.value || keyStr;
    }
    return keyStr; // Fallback to key if lookup data not available
  }

  /**
   * Determines the final license path based on intended path and license expiry status
   * @param intendedPath The intended path: '1' (Renewal) or '2' (Convert)
   * @returns Final license path: '1' (Renewal), '2' (Convert), or '4' (Reapply)
   */
  private determineLicensePath(intendedPath: string): string {
    const licenseData = this.dashboardData?.Obj_License?.[0];

    if (!licenseData || !licenseData.dtExpiryDate) {
      console.warn('[LICENSE PATH] License data not available, returning intended path:', intendedPath);
      return intendedPath;
    }

    const today = new Date();
    const expiryDate = new Date(licenseData.dtExpiryDate);
    const isExpired = this.stripTime(today) > this.stripTime(expiryDate);

    console.log('[LICENSE PATH] Date comparison:', {
      today: today.toISOString().split('T')[0],
      expiry: expiryDate.toISOString().split('T')[0],
      isExpired,
      intendedPath
    });

    if (isExpired) {
      // Only change to Reapply (4) if intended path was Renewal (1)
      // Convert (2) should remain Convert even if expired
      if (intendedPath === '1') {
        console.log('[LICENSE PATH] License expired and intended path is Renewal, returning Reapply (4)');
        return '4'; // Reapply if expired and was going to renew
      } else {
        console.log(`[LICENSE PATH] License expired but intended path is Convert, returning intended path (${intendedPath})`);
        return intendedPath; // Keep intended path (Convert) even if expired
      }
    } else {
      console.log(`[LICENSE PATH] License valid, returning intended path (${intendedPath})`);
      return intendedPath; // Return intended path if still valid
    }
  }

  // Method to load dashboard data and check renew conditions
  private loadDashboardDataAndCheckConditions(): void {
    const user = JSON.parse(this.sessionStorage.getItem('user') || '{}');
    const userId = user?.id;

    if (!userId) {
      console.log('User ID not found, skipping renew condition check');
      this.applicationTypesReady = true;
      this.loaderService.hide();
      this.cdr.detectChanges();
      return;
    }

    const payload = { userId };

    // Debug: Log applicationTypes before default block renders
      console.log('[RENEW CHECK] applicationTypes for default block:', this.applicationTypes);

    // Load both dashboard data AND applications data in parallel
    forkJoin([
      this.httpService.post(API_ENDPOINTS.DASHBOARD_DATA, payload),
      this.httpService.post(API_ENDPOINTS.applications, payload)
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: ([dashboardResponse, applicationsResponse]: any[]) => {
          // Store dashboard data
          if (dashboardResponse?.status === 'success' && dashboardResponse?.data?.data?.[0]) {
            this.dashboardData = dashboardResponse.data.data[0];
          }

          // Store applications data (contains cd_application_status)
          if (applicationsResponse?.status === 'success' && applicationsResponse?.data?.data) {
            this.applicationsData = applicationsResponse.data.data;
            console.log('[RENEW CHECK] Applications data loaded:', this.applicationsData.length, 'applications');
          }

          this.extractConditionalOptionConditions();

          // Check conditions after both data sources are loaded
          // But only if business rules are also loaded
          if (this.rcrBusinessRulesLoaded) {
            this.checkRenewConditions();
            this.restoreConditionalOptions();
          } else {
            // Mark that we need to check conditions once business rules are loaded
            this.pendingRenewConditionCheck = true;
            console.log('[RENEW CHECK] Waiting for business rules to load before checking conditions');
          }
          this.applicationTypesReady = true;
          this.loaderService.hide();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading dashboard data or applications:', error);
          // On error, don't show renew option (default behavior)
          this.showRenewOption = false;
          this.applicationTypesReady = true;
          this.loaderService.hide();
          this.cdr.detectChanges();
        }
      });
  }

  private checkRenewConditions(): void {
    console.log('[RENEW CHECK] Starting checkRenewConditions...');

    // Default to false
    this.showRenewOption = false;

    // CONDITION 1: Check if we have necessary dashboard data
    if (!this.dashboardData) {
      console.warn('[RENEW CHECK] CONDITION 1 FAILED: Missing dashboard data');
      return;
    }

    console.log('[RENEW CHECK] CONDITION 1 PASSED: Dashboard data available');

    // CONDITION 2: Check if we have applications data from applications API
    if (!this.applicationsData || !Array.isArray(this.applicationsData) || this.applicationsData.length === 0) {
      console.log('[RENEW CHECK] CONDITION 2 FAILED: No applications found in applications API');
      return;
    }

    console.log('[RENEW CHECK] CONDITION 2 PASSED: Total applications found:', this.applicationsData.length);

    // CONDITION 3: Filter applications with approved status (using numeric cd_application_status)
    const approvedApps = this.applicationsData.filter((app: any) =>
      app.cd_application_status === '9' || app.cd_application_status === '41' || app.cd_application_status === '40'
    );

    console.log('[RENEW CHECK] CONDITION 3: Approved applications found:', approvedApps.length);
    console.log('[RENEW CHECK] CONDITION 3: Approved applications:', approvedApps);

    if (approvedApps.length === 0) {
      console.log('[RENEW CHECK] CONDITION 3 FAILED: No approved applications found');
      return;
    }

    console.log('[RENEW CHECK] CONDITION 3 PASSED: At least one approved application exists');

    // Sort by date created to get most recent (descending order)
    approvedApps.sort((a: any, b: any) => {
      const dateA = new Date(a.created_date).getTime();
      const dateB = new Date(b.created_date).getTime();
      return dateB - dateA; // Most recent first
    });

    // Find the first approved application that is NOT 'License Extension' (key=7), 'Name Change' (key=5), or 'Add Endorsement' (key=12)
    // These application types should be skipped when determining RCR eligibility
    let mostRecentApprovedApp: any = null;
    let appTypeKey: string = '';
    let appType: string = '';

    for (const app of approvedApps) {
      const currentAppTypeKey = app.cd_application_type;
      const currentAppType = this.getApplicationTypeDisplayValue(currentAppTypeKey);

      console.log('[RENEW CHECK] Checking application:', {
        appId: app.application_id,
        appTypeKey: currentAppTypeKey,
        appType: currentAppType,
        createdDate: app.created_date
      });

      // Skip 'License Extension' (key=7), 'Name Change' (key=5), 'Add Endorsement' (key=12), and 'Surrender' (key=10)
      if (currentAppTypeKey === '7' || currentAppTypeKey === '5' || currentAppTypeKey === '12' || currentAppTypeKey === '10' ||
          currentAppType === 'License Extension' || currentAppType === 'Name Change' || currentAppType === 'Add Endorsement' || currentAppType === 'Surrender') {
        console.log('[RENEW CHECK] Skipping application - type is License Extension, Name Change, Add Endorsement, or Surrender');
        continue;
      }

      // Found a valid application type
      mostRecentApprovedApp = app;
      appTypeKey = currentAppTypeKey;
      appType = currentAppType || this.findValueByKey(this.applicationTypes, currentAppTypeKey);
      // Store the valid application's ID for later use in onContinue
      this.validRcrApplicationId = app.application_id;
      // Store the most recent active application's integer id for use as old_id in the create payload
      // (the most recent app overall, which is what the backend will clone from)
      this.mostRecentActiveAppId = app.application_id ?? null;
      console.log('[RENEW CHECK] mostRecentActiveAppId (for cloning):', this.mostRecentActiveAppId);
      console.log('[RENEW CHECK] Found valid application for RCR check:', {
        appId: app.application_id,
        appType: appType
      });
      console.log('[RENEW CHECK] Stored validRcrApplicationId:', this.validRcrApplicationId);
      break;
    }

    // If all approved applications are License Extension, Name Change, Add Endorsement, or Surrender, don't show RCR option
    if (!mostRecentApprovedApp) {
      console.log('[RENEW CHECK] FAILED: All approved applications are License Extension, Name Change, Add Endorsement, or Surrender - no valid application for RCR');
      return;
    }

    console.log('[RENEW CHECK] Most recent valid approved application:', mostRecentApprovedApp);
    console.log('[RENEW CHECK] Application type (from cd_application_type):', appType);

    // CONDITION 4 & 5: Check license data exists (AFTER finding valid application)
    console.log('[RENEW CHECK] Full dashboard data:', this.dashboardData);
    const allLicenses = this.dashboardData.Obj_License;
    console.log('[RENEW CHECK] All license data:', allLicenses);

    // Check if Obj_License array exists and has at least one license with valid dtExpiryDate
    if (!allLicenses || !Array.isArray(allLicenses) || allLicenses.length === 0) {
      console.warn('[RENEW CHECK] CONDITION 4/5 FAILED: No license data found');
      return;
    }

    const licensesWithExpiryDate = allLicenses.filter(license => license?.dtExpiryDate);
    if (licensesWithExpiryDate.length === 0) {
      console.warn('[RENEW CHECK] CONDITION 4/5 FAILED: No licenses with valid expiry date found:', {
        totalLicenses: allLicenses.length,
        licensesWithExpiry: licensesWithExpiryDate.length
      });
      return;
    }

    console.log('[RENEW CHECK] CONDITION 4/5 PASSED: Found', licensesWithExpiryDate.length, 'license(s) with expiry date');

    // CONDITION 6: Check if AT LEAST ONE license falls within the allowed business rule window
    console.log('[RENEW CHECK] Checking business rule date condition for all licenses...');
    const currentDate = new Date();
    let atLeastOneLicenseQualifies = false;
    let qualifyingLicense: any = null;

    for (const license of licensesWithExpiryDate) {
      const expiryDate = new Date(license.dtExpiryDate);
      console.log('[RENEW CHECK] Checking license:', {
        licenseType: license.strLicenseType,
        licenseNumber: license.strLicenseNumber,
        expiryDate: expiryDate.toISOString().split('T')[0],
        isExpired: this.stripTime(currentDate) > this.stripTime(expiryDate)
      });

      const shouldShowForThisLicense = this.shouldShowRcrFlow(appType, expiryDate);
      console.log('[RENEW CHECK] License qualifies for RCR:', shouldShowForThisLicense);

      if (shouldShowForThisLicense) {
        atLeastOneLicenseQualifies = true;
        qualifyingLicense = license;
        console.log('[RENEW CHECK] Found qualifying license:', {
          licenseType: license.strLicenseType,
          licenseNumber: license.strLicenseNumber,
          expiryDate: expiryDate.toISOString().split('T')[0]
        });
        break; // Found at least one, no need to check further
      }
    }

    if (!atLeastOneLicenseQualifies) {
      console.log('[RENEW CHECK] CONDITION 6 FAILED: None of the licenses fall within allowed days of expiration');
      console.log('[RENEW CHECK] CONDITIONS 1-5 PASSED, CONDITION 6 FAILED: Showing RCR flow without Renew option');
      // Show only Regular - Initial and Name Change (no Renew, Convert or Re-Apply option)
      this.applicationTypes = this.applicationTypes.filter(t => t.value === 'Regular - Initial' || t.value === 'Name Change');
      console.log('[RENEW CHECK] Final result - applicationTypes (without Renew):', this.applicationTypes);
      return;
    }

    console.log('[RENEW CHECK] CONDITION 6 PASSED: At least one license qualifies for RCR');

    // Log the qualifying license for reference
    if (qualifyingLicense) {
      const qualifyingExpiryDate = new Date(qualifyingLicense.dtExpiryDate);
      console.log('[RENEW CHECK] Qualifying license details:', {
        currentDate: currentDate.toISOString().split('T')[0],
        expiryDate: qualifyingExpiryDate.toISOString().split('T')[0],
        isExpired: this.stripTime(currentDate) > this.stripTime(qualifyingExpiryDate),
        willUsePath: this.stripTime(currentDate) <= this.stripTime(qualifyingExpiryDate) ? 'Renewal (path=1)' : 'Reapply (path=4)'
      });
    }

    console.log('[RENEW CHECK] CONDITION 6 PASSED: Current date is within allowed days of license expiration');

    // Show RCR option for all valid application types (including Apprentice)
    console.log('[RENEW CHECK] ALL CONDITIONS 1-6 MET! Showing full RCR option');
    // Show all 3 options: Renew, Convert or Re-Apply, Regular - Initial, Name Change
    const allTypes = [
      { value: 'Renew, Convert or Re-Apply', key: 'renew-convert-reapply' },
      ...this.applicationTypes.filter(t => t.value === 'Regular - Initial' || t.value === 'Name Change')
    ];
    this.applicationTypes = allTypes;

    console.log('[RENEW CHECK] Final result - applicationTypes (with Renew):', this.applicationTypes);
  }

  // Method to handle answer selection for qualifying questions
  async onAnswerChange(index: number, answer: 'yes' | 'no' | 'not applicable'): Promise<void> {
    this.qualifyingAnswers[index] = answer;
    this.qualifyingErrors[index] = ''; // Clear error for this question

    console.log(`[ANSWER CHANGE] Index: ${index}, Answer: ${answer}, Current type: ${this.currentApplicationType}`);

    // Handle conditional question logic for Substitute
    if (this.currentApplicationType === 'Substitute') {
      const questionKey = this.qualifyingQuestionKeys[index];
      console.log(`[ANSWER CHANGE] Question key at index ${index}: ${questionKey}`);
      console.log('[ANSWER CHANGE] All question keys:', this.qualifyingQuestionKeys);

      // If answering question 5 (bachelor's degree question)
      if (questionKey === '5') {
        console.log('[ANSWER CHANGE] Question 5 detected!');
        if (answer === 'no') {
          console.log('[ANSWER CHANGE] Q5 = No, fetching Q6...');
          // Fetch and show question 6
          await this.fetchAndAddQuestion('6', index);
        } else {
          console.log('[ANSWER CHANGE] Q5 = Yes, removing Q6/Q7...');
          // Remove questions 6 and 7 if they exist
          this.removeQuestionsAfterIndex(index);
        }
      }

      // If answering question 6 (license without completion question)
      if (questionKey === '6') {
        console.log('[ANSWER CHANGE] Question 6 detected!');
        if (answer === 'no') {
          console.log('[ANSWER CHANGE] Q6 = No, fetching Q7...');
          // Fetch and show question 7
          await this.fetchAndAddQuestion('7', index);
        } else {
          console.log('[ANSWER CHANGE] Q6 = Yes, removing Q7...');
          // Remove question 7 if it exists
          this.removeQuestionsAfterIndex(index);
        }
      }

      // If answering question 7, no further questions needed
      if (questionKey === '7') {
        // No additional questions to fetch
      }

      // For Substitute: Check ALL visible questions
      // Apply special logic for license requirement/provision questions
      // Conditional questions (5, 6, 7) can have "No" without blocking (they're not in license groups)

      // Group ALL visible questions by their keys
      const answersByKey = new Map<string, ('yes' | 'no' | 'not applicable' | null)[]>();
      this.visibleQuestionIndices.forEach(i => {
        const key = this.qualifyingQuestionKeys[i];
        if (!answersByKey.has(key)) {
          answersByKey.set(key, []);
        }
        answersByKey.get(key)!.push(this.qualifyingAnswers[i]);
      });

      // Check if there are any blocking "No" answers
      let hasBlockingNo = false;

      for (const [key, answers] of answersByKey.entries()) {
        // For license requirement or license provision questions:
        // Only block if ALL questions in the group are "No" (none are "Yes" or "Not Applicable")
        if (this.licenseRequirementKeys.has(key) || this.licenseProvisionKeys.has(key)) {
          const hasAtLeastOneYes = answers.some(ans => ans === 'yes');
          const hasAtLeastOneNotApplicable = answers.some(ans => ans === 'not applicable');
          const hasAtLeastOneNo = answers.some(ans => ans === 'no');

          // Block only if there's at least one "No" AND no "Yes" or "Not Applicable" answers
          if (hasAtLeastOneNo && !hasAtLeastOneYes && !hasAtLeastOneNotApplicable) {
            hasBlockingNo = true;
            console.log(`[SUBSTITUTE] Blocking - License req/prov key ${key}: All answered No (${answers.length} question(s))`);
            break;
          }
        }
        // Note: Conditional questions (5, 6, 7) are NOT in license groups, so they won't block
      }

      console.log('[SUBSTITUTE] Global error check - hasBlockingNo:', hasBlockingNo);

      if (hasBlockingNo) {
        this.qualifyingGlobalError = 'global';
      } else {
        this.qualifyingGlobalError = null;
      }
    } else if (this.currentApplicationType === 'Emergency Substitute') {
      const questionKey = this.qualifyingQuestionKeys[index];
      console.log(`[ANSWER CHANGE] Question key at index ${index}: ${questionKey}`);
      console.log('[ANSWER CHANGE] All question keys:', this.qualifyingQuestionKeys);

      // If answering question 4 (60 credit hours question)
      if (questionKey === '4') {
        console.log('[ANSWER CHANGE] Question 4 detected!');
        if (answer === 'yes') {
          console.log('[ANSWER CHANGE] Q4 = Yes, fetching Q5...');
          // Fetch and show question 5
          await this.fetchAndAddQuestion('5', index);
        } else {
          console.log('[ANSWER CHANGE] Q4 = No, removing Q5/Q6/Q7...');
          // Remove questions 5, 6, and 7 if they exist
          this.removeQuestionsAfterIndex(index);
        }
      }

      // If answering question 5
      if (questionKey === '5') {
        console.log('[ANSWER CHANGE] Question 5 detected!');
        if (answer === 'no') {
          console.log('[ANSWER CHANGE] Q5 = No, fetching Q6...');
          // Fetch and show question 6
          await this.fetchAndAddQuestion('6', index);
        } else {
          console.log('[ANSWER CHANGE] Q5 = Yes, removing Q6/Q7...');
          // Remove questions 6 and 7 if they exist
          this.removeQuestionsAfterIndex(index);
        }
      }

      // If answering question 6
      if (questionKey === '6') {
        console.log('[ANSWER CHANGE] Question 6 detected!');
        if (answer === 'no') {
          console.log('[ANSWER CHANGE] Q6 = No, fetching Q7...');
          // Fetch and show question 7
          await this.fetchAndAddQuestion('7', index);
        } else {
          console.log('[ANSWER CHANGE] Q6 = Yes, removing Q7...');
          // Remove question 7 if it exists
          this.removeQuestionsAfterIndex(index);
        }
      }

      // If answering question 7, no further questions needed
      if (questionKey === '7') {
        // No additional questions to fetch
      }

      // For Emergency Substitute: Check ALL visible questions
      // Apply special logic for license requirement/provision questions
      // Conditional questions (4, 5, 6, 7) can have "No" without blocking (they're not in license groups)

      // Group ALL visible questions by their keys
      const answersByKey = new Map<string, ('yes' | 'no' | 'not applicable' | null)[]>();
      this.visibleQuestionIndices.forEach(i => {
        const key = this.qualifyingQuestionKeys[i];
        if (!answersByKey.has(key)) {
          answersByKey.set(key, []);
        }
        answersByKey.get(key)!.push(this.qualifyingAnswers[i]);
      });

      // Check if there are any blocking "No" answers
      let hasBlockingNo = false;

      for (const [key, answers] of answersByKey.entries()) {
        // For license requirement or license provision questions:
        // Only block if ALL questions in the group are "No" (none are "Yes" or "Not Applicable")
        if (this.licenseRequirementKeys.has(key) || this.licenseProvisionKeys.has(key)) {
          const hasAtLeastOneYes = answers.some(ans => ans === 'yes');
          const hasAtLeastOneNotApplicable = answers.some(ans => ans === 'not applicable');
          const hasAtLeastOneNo = answers.some(ans => ans === 'no');

          // Block only if there's at least one "No" AND no "Yes" or "Not Applicable" answers
          if (hasAtLeastOneNo && !hasAtLeastOneYes && !hasAtLeastOneNotApplicable) {
            hasBlockingNo = true;
            console.log(`[EMERGENCY SUBSTITUTE] Blocking - License req/prov key ${key}: All answered No (${answers.length} question(s))`);
            break;
          }
        }
        // Note: Conditional questions (4, 5, 6, 7) are NOT in license groups, so they won't block
      }

      console.log('[EMERGENCY SUBSTITUTE] Global error check - hasBlockingNo:', hasBlockingNo);

      if (hasBlockingNo) {
        this.qualifyingGlobalError = 'global';
      } else {
        this.qualifyingGlobalError = null;
      }
    }

     // Handle ARL program fields visibility (for both ARL and Substitute + ARL)
    if (this.currentApplicationType === 'ARL (Alternative Route to Licensure)' ||
        this.currentApplicationType === 'Substitute + ARL') {
      const questionKey = this.qualifyingQuestionKeys[index];
      console.log(`[ARL] Question key at index ${index}: ${questionKey}`);

      // If answering question with key '2' (ARL program completion question)
      if (questionKey === '2') {
        console.log('[ARL] Question 2 detected (ARL program completion)!');
        if (answer === 'yes') {
          console.log('[ARL] Q2 = Yes, showing program fields...');
          this.showArlProgramFields = true;

          // If in RCR flow, pre-populate ARL data from the old application
          // Add delay to ensure fields are rendered in DOM before setting values
          if (this.oldApplicationIntegerId) {
            console.log('[ARL] RCR flow detected, fetching saved ARL data for application_id:', this.oldApplicationIntegerId);
            // Use setTimeout to allow Angular to render the fields first
            setTimeout(() => {
              this.loadArlProgramFields(this.oldApplicationIntegerId);
            }, 100);
          } else if (this.oldApplicationId) {
            console.warn('[ARL] oldApplicationId exists but oldApplicationIntegerId not found:', this.oldApplicationId);
          }
        } else {
          console.log('[ARL] Q2 = No, hiding program fields...');
          this.showArlProgramFields = false;
          // Clear field values when hidden
          this.arlProviderName = null;
          this.arlProgramArea = null;
          this.arlCompletionDate = null;
          this.arlProgramAreaError = '';
          this.arlCompletionDateError = '';
          this.arlProgramAreaTouched = false;
          this.arlCompletionDateTouched = false;
        }
      }
    }

    // For Regular - Initial, ARL, Substitute + ARL, Apprentice: Check if any VISIBLE answer is "No"
    // BUT apply special logic for license requirement and license provision questions
    if (this.currentApplicationType === 'Regular - Initial' ||
        this.currentApplicationType === 'ARL (Alternative Route to Licensure)' ||
        this.currentApplicationType === 'Substitute + ARL' ||
        this.currentApplicationType === 'Apprentice') {
      const visibleAnswers = this.qualifyingAnswers.filter((_, i) => this.visibleQuestionIndices.includes(i));

      // Group questions by their keys
      const answersByKey = new Map<string, ('yes' | 'no' | 'not applicable' | null)[]>();
      this.visibleQuestionIndices.forEach(i => {
        const key = this.qualifyingQuestionKeys[i];
        if (!answersByKey.has(key)) {
          answersByKey.set(key, []);
        }
        answersByKey.get(key)!.push(this.qualifyingAnswers[i]);
      });

      // Check if there are any blocking "No" answers
      let hasBlockingNo = false;

      for (const [key, answers] of answersByKey.entries()) {
        // For license requirement or license provision questions:
        // Only block if ALL questions in the group are "No" (none are "Yes" or "Not Applicable")
        if (this.licenseRequirementKeys.has(key) || this.licenseProvisionKeys.has(key)) {
          const hasAtLeastOneYes = answers.some(ans => ans === 'yes');
          const hasAtLeastOneNotApplicable = answers.some(ans => ans === 'not applicable');
          const hasAtLeastOneNo = answers.some(ans => ans === 'no');

          // Block only if there's at least one "No" AND no "Yes" or "Not Applicable" answers
          if (hasAtLeastOneNo && !hasAtLeastOneYes && !hasAtLeastOneNotApplicable) {
            hasBlockingNo = true;
            console.log(`[VALIDATION] Blocking - License req/prov key ${key}: All answered No`);
            break;
          }
        } else {
          // For other questions: Any "No" answer blocks (but "Not Applicable" is allowed)
          if (answers.some(ans => ans === 'no')) {
            hasBlockingNo = true;
            console.log(`[VALIDATION] Blocking - Regular question key ${key}: Has No answer`);
            break;
          }
        }
      }

      if (hasBlockingNo) {
        this.qualifyingGlobalError = 'global';
      } else {
        this.qualifyingGlobalError = null;
      }
    }
  }

  // Helper method to fetch a question by key and add it to the questions list
  private async fetchAndAddQuestion(key: string, afterIndex: number): Promise<void> {
    try {
      console.log(`[CONDITIONAL Q] Fetching question with key: ${key}`);
      const response: any = await this.httpService.get(`${API_ENDPOINTS.RCR_QUESTION_BY_KEY}?application_type=${this.currentApplicationType}&key=${key}`).toPromise();

      // Handle nested response structure (response.data.data.data)
      const questionData = response?.data?.data?.data || response?.data?.data;
      console.log(`[CONDITIONAL Q] Response structure:`, response);
      console.log(`[CONDITIONAL Q] Question data:`, questionData);

      if (response?.status === 'success' && questionData && Array.isArray(questionData) && questionData.length > 0) {
        const question = questionData[0];
        const newIndex = afterIndex + 1;

        // Check if question already exists at this position
        if (this.qualifyingQuestionKeys[newIndex] === key) {
          console.log(`[CONDITIONAL Q] Question ${key} already exists at index ${newIndex}`);
          if (!this.visibleQuestionIndices.includes(newIndex)) {
            this.visibleQuestionIndices.push(newIndex);
            this.visibleQuestionIndices.sort((a, b) => a - b);
          }
          return;
        }

        // Insert the new question after the current index
        this.qualifyingQuestions.splice(newIndex, 0, question.value);
        this.qualifyingQuestionKeys.splice(newIndex, 0, question.key);
        this.qualifyingAnswers.splice(newIndex, 0, null);
        this.qualifyingErrors.splice(newIndex, 0, '');

        // Update visible indices
        this.visibleQuestionIndices = this.visibleQuestionIndices
          .map(i => i > afterIndex ? i + 1 : i)
          .concat([newIndex])
          .sort((a, b) => a - b);

        console.log(`[CONDITIONAL Q] Added question ${key} at index ${newIndex}`);
        console.log('[CONDITIONAL Q] Visible indices:', this.visibleQuestionIndices);
      } else {
        console.warn(`[CONDITIONAL Q] Failed to fetch question ${key}:`, response);
      }
    } catch (error) {
      console.error(`[CONDITIONAL Q] Error fetching question ${key}:`, error);
    }
  }

  // Helper method to remove questions after a given index
  private removeQuestionsAfterIndex(index: number): void {
    // Remove from visible indices
    this.visibleQuestionIndices = this.visibleQuestionIndices.filter(i => i <= index);

    // Clear answers for hidden questions
    for (let i = index + 1; i < this.qualifyingAnswers.length; i++) {
      if (!this.visibleQuestionIndices.includes(i)) {
        this.qualifyingAnswers[i] = null;
        this.qualifyingErrors[i] = '';
      }
    }

    console.log('[CONDITIONAL Q] Removed questions after index', index);
    console.log('[CONDITIONAL Q] Visible indices:', this.visibleQuestionIndices);
  }

  // Validation methods for ARL Program fields
  validateArlProgramArea(): void {
    this.arlProgramAreaTouched = true;
    if (!this.arlProgramArea || this.arlProgramArea === 'null') {
      this.arlProgramAreaError = 'M0152'; // Use error message key
    } else {
      this.arlProgramAreaError = '';
    }
  }

  // Format date input as MM/DD/YYYY (similar to decision-role-page component)
  formatArlDateInput(event: any): void {
    const domEvent = event?.originalEvent || event;
    const target = domEvent?.target || event?.target;
    if (!target) {
      return;
    }

    const inputType = String(domEvent?.inputType || '');
    const isDeleteAction = inputType.startsWith('delete');
    const currentValue = String(target.value || '');

    let value = '';
    if (isDeleteAction) {
      // Keep user deletion as-is; do not re-insert "/" while backspacing.
      value = currentValue.replace(/[^\d/]/g, '').slice(0, 10);
    } else {
      const digitsOnly = currentValue.replace(/\D/g, '').slice(0, 8);
      if (digitsOnly.length <= 2) {
        value = digitsOnly;
      } else if (digitsOnly.length <= 4) {
        value = `${digitsOnly.substring(0, 2)}/${digitsOnly.substring(2)}`;
      } else {
        value = `${digitsOnly.substring(0, 2)}/${digitsOnly.substring(2, 4)}/${digitsOnly.substring(4)}`;
      }
    }

    target.value = value;
    this.arlCompletionDate = value;
  }

  validateArlCompletionDate(): void {
    this.arlCompletionDateTouched = true;

    // Check if empty
    if (!this.arlCompletionDate) {
      this.arlCompletionDateError = 'M0145'; // Use error message key for required field
      return;
    }

    // Check if valid date format MM/DD/YYYY
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;
    if (!dateRegex.test(this.arlCompletionDate)) {
      this.arlCompletionDateError = 'M0156'; // Use error message key for invalid date
      return;
    }

    // Parse the date
    const parts = this.arlCompletionDate.split('/');
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Check if valid date (e.g., not 02/31/2024)
    const dateValue = new Date(year, month - 1, day);
    if (dateValue.getMonth() + 1 !== month || dateValue.getDate() !== day || dateValue.getFullYear() !== year) {
      this.arlCompletionDateError = 'M0156'; // Use error message key for invalid date
      return;
    }

    // Check if date is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate comparison
    dateValue.setHours(0, 0, 0, 0);

    if (dateValue > today) {
      this.arlCompletionDateError = 'M0156'; // Use error message key for future date
      return;
    }

    // All validations passed
    this.arlCompletionDateError = '';
  }

  // Open date picker for ARL Completion Date (similar to decision-role-page component)
  openArlDatePicker(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const anchorInput = document.getElementById('arlCompletionDateInput');
    if (!anchorInput) {
      return;
    }

    const rect = anchorInput.getBoundingClientRect();

    const dateInput = document.createElement('input');
    dateInput.type = 'date';
    dateInput.style.position = 'absolute';
    dateInput.style.top = `${rect.top + window.scrollY + 5}px`;
    dateInput.style.left = `${rect.left}px`;
    dateInput.style.width = '120px';
    dateInput.style.height = '35px';
    dateInput.style.opacity = '0.01';
    dateInput.style.border = 'none';
    dateInput.style.zIndex = '9999';
    dateInput.style.pointerEvents = 'auto';

    // Set max date to today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    dateInput.max = `${year}-${month}-${day}`;

    // Set current value if exists
    const currentValue = this.arlCompletionDate;
    if (typeof currentValue === 'string' && currentValue.includes('/')) {
      const parts = currentValue.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        const monthPart = parts[0].padStart(2, '0');
        const dayPart = parts[1].padStart(2, '0');
        const yearPart = parts[2];
        dateInput.value = `${yearPart}-${monthPart}-${dayPart}`;
      }
    }

    document.body.appendChild(dateInput);

    const cleanup = () => {
      if (document.body.contains(dateInput)) {
        dateInput.removeEventListener('change', changeHandler);
        dateInput.removeEventListener('blur', blurHandler);
        document.body.removeChild(dateInput);
      }
    };

    const changeHandler = (changeEvent: any) => {
      const selectedDate = changeEvent.target.value;
      if (selectedDate) {
        const dateParts = selectedDate.split('-');
        if (dateParts.length === 3) {
          const yearPart = dateParts[0];
          const monthPart = dateParts[1];
          const dayPart = dateParts[2];
          const formattedDate = `${monthPart}/${dayPart}/${yearPart}`;

          this.arlCompletionDate = formattedDate;
          this.validateArlCompletionDate();
        }
      }
      cleanup();
    };

    const blurHandler = () => {
      setTimeout(() => cleanup(), 200);
    };

    dateInput.addEventListener('change', changeHandler);
    dateInput.addEventListener('blur', blurHandler);

    setTimeout(() => {
      dateInput.focus();
      dateInput.showPicker?.();
    }, 10);
  }

  // Method to validate qualifying questions before proceeding
  canProceedWithQualifying(): boolean {
    // Only check visible questions
    const visibleAnswers = this.visibleQuestionIndices.map(i => this.qualifyingAnswers[i]);

    // Check if all VISIBLE questions are answered
    const allVisibleAnswered = visibleAnswers.every(ans => ans !== null);
    if (!allVisibleAnswered) {
      // Set error for unanswered VISIBLE questions
      this.visibleQuestionIndices.forEach((visibleIndex) => {
        if (this.qualifyingAnswers[visibleIndex] === null) {
          this.qualifyingErrors[visibleIndex] = AppConstants.pleaseSelectAnOption;
        }
      });
      return false;
    }

    // For Regular - Initial, ARL, Substitute + ARL, and Apprentice: Check if any visible answer is "No"
    // BUT apply special logic for license requirement and license provision questions
    if (this.currentApplicationType === 'Regular - Initial' ||
        this.currentApplicationType === 'ARL (Alternative Route to Licensure)' ||
        this.currentApplicationType === 'Substitute + ARL' ||
        this.currentApplicationType === 'Apprentice') {

      // Group visible questions by their keys
      const answersByKey = new Map<string, ('yes' | 'no' | 'not applicable' | null)[]>();
      this.visibleQuestionIndices.forEach(i => {
        const key = this.qualifyingQuestionKeys[i];
        if (!answersByKey.has(key)) {
          answersByKey.set(key, []);
        }
        answersByKey.get(key)!.push(this.qualifyingAnswers[i]);
      });

      // Check if there are any blocking "No" answers
      let hasBlockingNo = false;

      for (const [key, answers] of answersByKey.entries()) {
        // For license requirement or license provision questions:
        // Only block if ALL questions in the group are "No" (none are "Yes" or "Not Applicable")
        if (this.licenseRequirementKeys.has(key) || this.licenseProvisionKeys.has(key)) {
          const hasAtLeastOneYes = answers.some(ans => ans === 'yes');
          const hasAtLeastOneNotApplicable = answers.some(ans => ans === 'not applicable');
          const hasAtLeastOneNo = answers.some(ans => ans === 'no');

          // Block only if there's at least one "No" AND no "Yes" or "Not Applicable" answers
          if (hasAtLeastOneNo && !hasAtLeastOneYes && !hasAtLeastOneNotApplicable) {
            hasBlockingNo = true;
            console.log(`[QUALIFYING] Blocking - License req/prov key ${key}: All answered No`);
            break;
          }
        } else {
          // For other questions: Any "No" answer blocks ("Not Applicable" is allowed)
          if (answers.some(ans => ans === 'no')) {
            hasBlockingNo = true;
            console.log(`[QUALIFYING] Blocking - Regular question key ${key}: Has No answer`);
            break;
          }
        }
      }

      if (hasBlockingNo) {
        this.qualifyingGlobalError = 'global';
        return false;
      }
    }

    // For Substitute and Emergency Substitute: Check ALL visible questions
    // Apply special logic for license requirement/provision questions
    // Conditional questions can have "No" without blocking (they're not in license groups)
    if (this.currentApplicationType === 'Substitute' || this.currentApplicationType === 'Emergency Substitute') {
      // Group ALL visible questions by their keys
      const answersByKey = new Map<string, ('yes' | 'no' | 'not applicable' | null)[]>();
      this.visibleQuestionIndices.forEach(i => {
        const key = this.qualifyingQuestionKeys[i];
        if (!answersByKey.has(key)) {
          answersByKey.set(key, []);
        }
        answersByKey.get(key)!.push(this.qualifyingAnswers[i]);
      });

      // Check if there are any blocking "No" answers
      let hasBlockingNo = false;

      for (const [key, answers] of answersByKey.entries()) {
        // For license requirement or license provision questions:
        // Only block if ALL questions in the group are "No" (none are "Yes" or "Not Applicable")
        if (this.licenseRequirementKeys.has(key) || this.licenseProvisionKeys.has(key)) {
          const hasAtLeastOneYes = answers.some(ans => ans === 'yes');
          const hasAtLeastOneNotApplicable = answers.some(ans => ans === 'not applicable');
          const hasAtLeastOneNo = answers.some(ans => ans === 'no');

          // Block only if there's at least one "No" AND no "Yes" or "Not Applicable" answers
          if (hasAtLeastOneNo && !hasAtLeastOneYes && !hasAtLeastOneNotApplicable) {
            hasBlockingNo = true;
            console.log(`[QUALIFYING] Blocking - License req/prov key ${key}: All answered No (${answers.length} question(s))`);
            break;
          }
        }
        // Note: Conditional questions are NOT in license groups, so they won't block
      }

      if (hasBlockingNo) {
        this.qualifyingGlobalError = 'global';
        return false;
      }
    }

    // Validate ARL Program fields if they are visible
    if (this.showArlProgramFields) {
      this.validateArlProgramArea();
      this.validateArlCompletionDate();

      // Check if there are any validation errors
      if (this.arlProgramAreaError || this.arlCompletionDateError) {
        console.log('[QUALIFYING] ARL field validation failed');
        return false;
      }
    }

    return true;
  }

  // Getter to check if all visible questions are answered (for button disable state)
  get areAllVisibleQuestionsAnswered(): boolean {
    const visibleAnswers = this.visibleQuestionIndices.map(i => this.qualifyingAnswers[i]);
    return visibleAnswers.every(ans => ans !== null);
  }
  // Updated onContinue for qualifying questions flow
  async onQualifyingContinue() {
     if (this.isCreatingApplication) {
      console.log('[QUALIFYING] Application creation already in progress, ignoring duplicate click');
      return;
    }
    if (!this.canProceedWithQualifying()) {
      console.log('[QUALIFYING] Validation failed');
      this.isCreatingApplication = false;
      return;
    }
    this.isCreatingApplication = true;

    console.log('[QUALIFYING] All questions answered with Yes, proceeding...');
    console.log('[QUALIFYING] Answers:', this.qualifyingAnswers);

    // Store license-application-type and license-path based on most recent approved application type
    if (this.dashboardData && Array.isArray(this.dashboardData.li_Application) && this.dashboardData.li_Application.length > 0) {
      // oldApplicationId should already be set from onContinue() using validRcrApplicationId
      // We just need to get the application type to determine the flow
      console.log('[QUALIFYING] Using already stored oldApplicationId:', this.oldApplicationId);

      // Filter approved applications
      const approvedApps = this.dashboardData.li_Application.filter((app: any) =>
        app.strAppStatus === 'Approved' || app.strAppStatus === 'Partially Approved'
      );

      if (approvedApps.length === 0) {
        console.warn('[QUALIFYING] No approved applications found');
        this.isCreatingApplication = false;
        return;
      }

      // Sort by date created to get most recent (descending order)
      approvedApps.sort((a: any, b: any) => {
        const dateA = new Date(a.dtDateCreated).getTime();
        const dateB = new Date(b.dtDateCreated).getTime();
        return dateB - dateA; // Most recent first
      });

      // Find the first approved application that is NOT 'License Extension', 'Name Change', or 'Add Endorsement'
      let validApp: any = null;
      for (const app of approvedApps) {
        const appType = app.strAppType;
        console.log('[QUALIFYING] Checking application:', {
          appId: app.strAppId,
          appType: appType,
          createdDate: app.dtDateCreated
        });

        // Skip 'License Extension', 'Name Change', 'Add Endorsement', and 'Surrender'
        if (appType === 'License Extension' || appType === 'Name Change' || appType === 'Add Endorsement' || appType === 'Surrender') {
          console.log('[QUALIFYING] Skipping application - type is License Extension, Name Change, Add Endorsement, or Surrender');
          continue;
        }

        // Found a valid application type
        validApp = app;
        console.log('[QUALIFYING] Found valid application:', {
          appId: app.strAppId,
          appType: appType
        });
        break;
      }

      // If all approved applications are License Extension, Name Change, Add Endorsement, or Surrender, exit
      if (!validApp) {
        console.warn('[QUALIFYING] FAILED: All approved applications are License Extension, Name Change, Add Endorsement, or Surrender');
        this.isCreatingApplication = false;
        return;
      }

      const applicationType = validApp.strAppType;
      console.log('[QUALIFYING] Most recent approved application type:', applicationType);

      switch (applicationType) {
        case 'Regular - Initial':
          // Store display value and set selectedType to '3' for database
          this.displayApplicationType = 'Regular - Initial';
          this.selectedType = '3';
          // oldApplicationId is already set from onContinue() using validRcrApplicationId

          // Determine license path (Renewal vs Reapply based on expiry)
          this.licensePath = this.determineLicensePath('1');

          // Store database keys in session storage
          this.sessionStorage.setItem('license-application-type', '3'); // Database key for Regular - Initial
          this.sessionStorage.setItem('license-path', this.licensePath); // Database value for Renewal or Reapply
          console.log(`[QUALIFYING] Stored: license-application-type = 3, license-path = ${this.licensePath}`);
          console.log('[QUALIFYING] Set oldApplicationId:', this.oldApplicationId);
          console.log('[QUALIFYING] Set licensePath:', this.licensePath);
          console.log('[QUALIFYING] licensePath type:', typeof this.licensePath);
          break;

        case 'Substitute':
          // Get answer indices for conditional questions
          const q5Index = this.qualifyingQuestionKeys.findIndex(key => key === '5');
          const q6Index = this.qualifyingQuestionKeys.findIndex(key => key === '6');
          const q7Index = this.qualifyingQuestionKeys.findIndex(key => key === '7');

          const q5Answer = q5Index !== -1 ? this.qualifyingAnswers[q5Index] : null;
          const q6Answer = q6Index !== -1 ? this.qualifyingAnswers[q6Index] : null;
          const q7Answer = q7Index !== -1 ? this.qualifyingAnswers[q7Index] : null;

          console.log('[QUALIFYING] Substitute answers - Q5:', q5Answer, 'Q6:', q6Answer, 'Q7:', q7Answer);

          // Determine application_type and license_path based on answers
          if (q5Answer === 'yes') {
            // Q5=Yes → app_type=3, path=2 (or 4 if expired)
            this.selectedType = '3';
            this.licensePath = this.determineLicensePath('2');
            this.displayApplicationType = 'Regular - Initial';
          } else if (q5Answer === 'no' && q6Answer === 'yes') {
            // Q5=No, Q6=Yes → app_type=3, path=2 (or 4 if expired)
            this.selectedType = '3';
            this.licensePath = this.determineLicensePath('2');
            this.displayApplicationType = 'Regular - Initial';
          } else if (q5Answer === 'no' && q6Answer === 'no' && q7Answer === 'yes') {
            // Q5=No, Q6=No, Q7=Yes → app_type=9, path=1 (or 4 if expired)
            this.selectedType = '9';
            this.licensePath = this.determineLicensePath('1');
            this.displayApplicationType = 'Substitute + ARL';
          } else if (q5Answer === 'no' && q6Answer === 'no' && q7Answer === 'no') {
            // Q5=No, Q6=No, Q7=No → app_type=6, path=1 (or 4 if expired)
            this.selectedType = '6';
            this.licensePath = this.determineLicensePath('1');
            this.displayApplicationType = 'Substitute';
          } else {
            console.warn('[QUALIFYING] Invalid answer combination for Substitute');
            this.isCreatingApplication = false;
            return;
          }
          // oldApplicationId is already set from onContinue() using validRcrApplicationId

          // Store database keys in session storage
          this.sessionStorage.setItem('license-application-type', this.selectedType);
          this.sessionStorage.setItem('license-path', this.licensePath);
          console.log(`[QUALIFYING] Stored: license-application-type = ${this.selectedType}, license-path = ${this.licensePath}`);
          console.log('[QUALIFYING]  Set oldApplicationId:', this.oldApplicationId);
          console.log('[QUALIFYING] Set licensePath:', this.licensePath);
          break;

        case 'Emergency Substitute':
          // Get answer indices for conditional questions
          const q4Index = this.qualifyingQuestionKeys.findIndex(key => key === '4');
          const q5IndexES = this.qualifyingQuestionKeys.findIndex(key => key === '5');
          const q6IndexES = this.qualifyingQuestionKeys.findIndex(key => key === '6');
          const q7IndexES = this.qualifyingQuestionKeys.findIndex(key => key === '7');

          const q4Answer = q4Index !== -1 ? this.qualifyingAnswers[q4Index] : null;
          const q5AnswerES = q5IndexES !== -1 ? this.qualifyingAnswers[q5IndexES] : null;
          const q6AnswerES = q6IndexES !== -1 ? this.qualifyingAnswers[q6IndexES] : null;
          const q7AnswerES = q7IndexES !== -1 ? this.qualifyingAnswers[q7IndexES] : null;

          console.log('[QUALIFYING] Emergency Substitute answers - Q4:', q4Answer, 'Q5:', q5AnswerES, 'Q6:', q6AnswerES, 'Q7:', q7AnswerES);

          // Determine application_type and license_path based on answers
          if (q4Answer === 'no') {
            // Q4=No → app_type=1, path=1 (or 4 if expired)
            this.selectedType = '1';
            this.licensePath = this.determineLicensePath('1');
            this.displayApplicationType = 'Emergency Substitute';
          } else if (q4Answer === 'yes' && q5AnswerES === 'yes') {
            // Q4=Yes, Q5=Yes → app_type=3, path=2 (or 4 if expired)
            this.selectedType = '3';
            this.licensePath = this.determineLicensePath('2');
            this.displayApplicationType = 'Regular - Initial';
          } else if (q4Answer === 'yes' && q5AnswerES === 'no' && q6AnswerES === 'yes') {
            // Q4=Yes, Q5=No, Q6=Yes → app_type=3, path=2 (or 4 if expired)
            this.selectedType = '3';
            this.licensePath = this.determineLicensePath('2');
            this.displayApplicationType = 'Regular - Initial';
          } else if (q4Answer === 'yes' && q5AnswerES === 'no' && q6AnswerES === 'no' && q7AnswerES === 'yes') {
            // Q4=Yes, Q5=No, Q6=No, Q7=Yes → app_type=9, path=2 (or 4 if expired)
            this.selectedType = '9';
            this.licensePath = this.determineLicensePath('2');
            this.displayApplicationType = 'Substitute + ARL';
          } else if (q4Answer === 'yes' && q5AnswerES === 'no' && q6AnswerES === 'no' && q7AnswerES === 'no') {
            // Q4=Yes, Q5=No, Q6=No, Q7=No → app_type=6, path=2 (or 4 if expired)
            this.selectedType = '6';
            this.licensePath = this.determineLicensePath('2');
            this.displayApplicationType = 'Substitute';
          } else {
            console.warn('[QUALIFYING] Invalid answer combination for Emergency Substitute');
            this.isCreatingApplication = false;
            return;
          }
          // oldApplicationId is already set from onContinue() using validRcrApplicationId

          // Store database keys in session storage
          this.sessionStorage.setItem('license-application-type', this.selectedType);
          this.sessionStorage.setItem('license-path', this.licensePath);
          console.log(`[QUALIFYING] Stored: license-application-type = ${this.selectedType}, license-path = ${this.licensePath}`);
          console.log('[QUALIFYING]  Set oldApplicationId:', this.oldApplicationId);
          console.log('[QUALIFYING]  Set licensePath:', this.licensePath);
          break;

        case 'ARL (Alternative Route to Licensure)':
          this.displayApplicationType = 'ARL (Alternative Route to Licensure)';
          this.selectedType = '3';
          // oldApplicationId is already set from onContinue() using validRcrApplicationId

        //   // Compare current date with license expiry date to determine license_path
        //   const qualifyingRequirementsARL = this.getQualifyingRequirements();
        //   const qualifyingProvisionsARL = this.getQualifyingProvisions();
        //   const hasDueDatesARL = qualifyingRequirements.length > 0 || qualifyingProvisions.length > 0;

        //   const licenseDataARL = this.dashboardData.Obj_License;
        //   if (licenseDataARL && licenseDataARL.dtExpiryDate) {
        //     const currentDate = new Date();
        //     const expiryDate = new Date(licenseDataARL.dtExpiryDate);

        //     let comparisonDate: Date;

        //     if (hasDueDatesARL) {
        //       // Use due dates for comparison - each requirement/provision due date will be checked
        //       const allDueDates: Date[] = [];

        //       qualifyingRequirementsARL.forEach(req => {
        //         if (req.dueDate) {
        //           const parts = req.dueDate.split('/');
        //           allDueDates.push(new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1])));
        //         }
        //       });

            //   qualifyingProvisionsARL.forEach(prov => {
            //     if (prov.dueDate) {
            //       const parts = prov.dueDate.split('/');
            //       allDueDates.push(new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1])));
            //     }
            //   });

            //   // Use the latest due date for comparison
            //   comparisonDate = new Date(Math.max(...allDueDates.map(d => d.getTime())));
            //   console.log('[QUALIFYING] ARL: Using due date for comparison:', comparisonDate.toISOString().split('T')[0]);
            // } else {
            //   // No due dates, use current date
            //   comparisonDate = new Date();
            //   console.log('[QUALIFYING] ARL: No due dates, using current date for comparison');
            // }

            // // If current date < expiry date: Renewal (1), otherwise: Reapply (4)
            // if (comparisonDate < expiryDate) {
            //   this.licensePath = '1'; // Renewal - license still valid
            //   console.log('[QUALIFYING] ARL: License still valid, setting Renewal (path=1)');
            // } else {
            //   this.licensePath = '4'; // Reapply - license expired
            //   console.log('[QUALIFYING] ARL: License expired, setting Reapply (path=4)');
            // }

        //     console.log('[QUALIFYING] ARL: Date comparison - Current:', comparisonDate.toISOString().split('T')[0], 'Expiry:', expiryDate.toISOString().split('T')[0]);
        //   } else {
        //     // Default to Renewal if license data not available
        //     this.licensePath = '1';
        //     console.warn('[QUALIFYING] ARL: License data not available, defaulting to Renewal (path=1)');
        //   }


        // For ARL, license_path is '2' (or '4' if expired)
          this.licensePath = this.determineLicensePath('2');
          console.log('[QUALIFYING] ARL: Set license_path =', this.licensePath);

          // Store database keys in session storage
          this.sessionStorage.setItem('license-application-type', this.selectedType);
          this.sessionStorage.setItem('license-path', this.licensePath);

          // Store ARL program fields in session storage if provided
          if (this.showArlProgramFields && this.arlProviderName) {
            this.sessionStorage.setItem('arl-provider-name', this.arlProviderName);
            console.log('[QUALIFYING] ARL: Stored arl-provider-name =', this.arlProviderName);
          }
          if (this.showArlProgramFields && this.arlProgramArea) {
            this.sessionStorage.setItem('arl-program-area', this.arlProgramArea);
            console.log('[QUALIFYING] ARL: Stored arl-program-area =', this.arlProgramArea);
          }
          if (this.showArlProgramFields && this.arlCompletionDate) {
            this.sessionStorage.setItem('arl-completion-date', this.arlCompletionDate);
            console.log('[QUALIFYING] ARL: Stored arl-completion-date =', this.arlCompletionDate);
          }

          console.log(`[QUALIFYING] Stored: license-application-type = ${this.selectedType}, license-path = ${this.licensePath}`);
          console.log('[QUALIFYING]  Set oldApplicationId:', this.oldApplicationId);
          console.log('[QUALIFYING]  Set licensePath:', this.licensePath);
          break;

          case 'Substitute + ARL':
          this.displayApplicationType = 'Substitute + ARL';
          this.selectedType = '3'; // Database key for Regular - Initial (application created as Regular - Initial)
          // oldApplicationId is already set from onContinue() using validRcrApplicationId

          // For Substitute + ARL, license_path follows same logic as ARL: '2' (or stays '2' if expired per determineLicensePath for Convert)
          this.licensePath = this.determineLicensePath('2');
          console.log('[QUALIFYING] Substitute + ARL: Set license_path =', this.licensePath);

          // Store database keys in session storage
          this.sessionStorage.setItem('license-application-type', '3'); // Regular - Initial
          this.sessionStorage.setItem('license-path', this.licensePath);

          // Store ARL program fields in session storage if provided
          if (this.showArlProgramFields && this.arlProviderName) {
            this.sessionStorage.setItem('arl-provider-name', this.arlProviderName);
            console.log('[QUALIFYING] Substitute + ARL: Stored arl-provider-name =', this.arlProviderName);
          }
          if (this.showArlProgramFields && this.arlProgramArea) {
            this.sessionStorage.setItem('arl-program-area', this.arlProgramArea);
            console.log('[QUALIFYING] Substitute + ARL: Stored arl-program-area =', this.arlProgramArea);
          }
          if (this.showArlProgramFields && this.arlCompletionDate) {
            this.sessionStorage.setItem('arl-completion-date', this.arlCompletionDate);
            console.log('[QUALIFYING] Substitute + ARL: Stored arl-completion-date =', this.arlCompletionDate);
          }

          console.log(`[QUALIFYING] Stored: license-application-type = 3 (Regular - Initial), license-path = ${this.licensePath}`);
          console.log('[QUALIFYING] Set oldApplicationId:', this.oldApplicationId);
          console.log('[QUALIFYING] Set licensePath:', this.licensePath);
          break;

        case 'Apprentice':
          this.displayApplicationType = 'Regular - Initial';
          this.selectedType = '3';
          // oldApplicationId is already set from onContinue() using validRcrApplicationId

          // For Apprentice, license_path is '2' (Convert) - stays '2' even if expired
          this.licensePath = this.determineLicensePath('2');
          console.log('[QUALIFYING] Apprentice: Set license_path =', this.licensePath);

          // Store database keys in session storage
          this.sessionStorage.setItem('license-application-type', this.selectedType);
          this.sessionStorage.setItem('license-path', this.licensePath);
          console.log(`[QUALIFYING] Stored: license-application-type = ${this.selectedType}, license-path = ${this.licensePath}`);
          console.log('[QUALIFYING] Set oldApplicationId:', this.oldApplicationId);
          console.log('[QUALIFYING] Set licensePath:', this.licensePath);
          break;

        default:
          console.warn('[QUALIFYING] Unknown application type:', applicationType);
          this.isCreatingApplication = false;
          return;
      }

      console.log('[QUALIFYING] selectedType set to:', this.selectedType);
      console.log('[QUALIFYING] displayApplicationType set to:', this.displayApplicationType);

      // Setup selectTypeValue with display application type for session storage and banner
      // this.selectTypeValue = [{ key: this.selectedType, value: this.displayApplicationType }];
      const newApplicationType = this.applicationTypes.find(t => t.key === this.selectedType);
      this.selectTypeValue = [{
        key: this.selectedType,
        value: newApplicationType?.value || this.displayApplicationType
      }];
      console.log('[QUALIFYING] selectTypeValue:', newApplicationType?.value || this.displayApplicationType);
    }

    // Proceed with normal flow
    try {
      this.isCreatingApplication = false;
      await this.getPerson();
      this.dialogRef.close(this.selectedType);
    } catch (error) {
      this.isCreatingApplication = false;
      console.error('Error in qualifying continue flow:', error);
    }
  }

  private fetchAndSetApplicationFee(licensePath: string): void {
    this.httpService.get('/domain/lookup?requestType=FEE_CATEGORY').pipe(take(1)).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        const feeKeyMap: Record<string, string> = { '1': '2', '2': '7', '3': '1', '4': '8' };
        const feeKey = feeKeyMap[licensePath] || '1';
        const feeRow = data.find((item: any) => String(item.key) === feeKey);
        if (feeRow) {
          const opt = typeof feeRow.optionaldata === 'string' ? JSON.parse(feeRow.optionaldata) : feeRow.optionaldata;
          const baseFee = Number(opt?.fee) || 0;
          this.sessionStorage.setItem('applicationTotalFee', JSON.stringify(baseFee));
          this.sessionStorage.setItem('applicationFee', JSON.stringify(baseFee));
        }
        const bgCheckRow = data.find((item: any) => String(item.key) === '6');
        if (bgCheckRow) {
          const bgOpt = typeof bgCheckRow.optionaldata === 'string' ? JSON.parse(bgCheckRow.optionaldata) : bgCheckRow.optionaldata;
          this.sessionStorage.setItem('backgroundCheckFee', JSON.stringify(Number(bgOpt?.fee) || 0));
        }
        const hasApprovedExtension = JSON.parse(this.sessionStorage.getItem('has_approved_extension') || 'false');
        if (licensePath === '1' && hasApprovedExtension) {
          const extRow = data.find((item: any) => String(item.key) === '4');
          if (extRow) {
            const extOpt = typeof extRow.optionaldata === 'string' ? JSON.parse(extRow.optionaldata) : extRow.optionaldata;
            this.sessionStorage.setItem('extensionFee', JSON.stringify(Number(extOpt?.fee) || 0));
          }
        }
      }
    });
  }

  /**
   * Check if any license in the License Details table has License Type = 'Provisional'
   */
  private hasProvisionalLicenseType(): boolean {
    if (!this.dashboardData) {
      console.log('[PROVISIONS CHECK] No dashboard data available');
      return false;
    }

    // Check in Obj_License array
    let hasProvisionalInLicense = false;
    if (this.dashboardData.Obj_License) {
      const licenses = Array.isArray(this.dashboardData.Obj_License)
        ? this.dashboardData.Obj_License
        : [this.dashboardData.Obj_License];

      hasProvisionalInLicense = licenses.some((license: any) => {
        const licenseType = license?.strLicenseType || '';
        const endoStatus = license?.strEndoStatus || '';
        const isProvisional = licenseType.toLowerCase() === 'provisional';
        const isActive = endoStatus.toLowerCase() === 'active';

        console.log('[PROVISIONS CHECK] Obj_License item:', {
          licenseNumber: license?.strLicenseNumber,
          licenseType: licenseType,
          endoStatus: endoStatus,
          isProvisional: isProvisional,
          isActive: isActive
        });

        return isProvisional && isActive;
      });

      console.log('[PROVISIONS CHECK] Has Provisional in Obj_License with Active endoStatus:', hasProvisionalInLicense);
    }

    // Check in Obj_LicenseKind array
    let hasProvisionalInLicenseKind = false;
    if (this.dashboardData.Obj_LicenseKind) {
      const licenseKinds = Array.isArray(this.dashboardData.Obj_LicenseKind)
        ? this.dashboardData.Obj_LicenseKind
        : [this.dashboardData.Obj_LicenseKind];

      hasProvisionalInLicenseKind = licenseKinds.some((licenseKind: any) => {
        const licenseType = licenseKind?.strLicenseType || '';
        const licenseKindStatus = licenseKind?.strLicenseKindStatus || '';
        const isProvisional = licenseType.toLowerCase() === 'provisional';
        const isActive = licenseKindStatus.toLowerCase() === 'active';

        console.log('[PROVISIONS CHECK] Obj_LicenseKind item:', {
          licenseKind: licenseKind?.strLicenseKind,
          licenseType: licenseType,
          licenseKindStatus: licenseKindStatus,
          isProvisional: isProvisional,
          isActive: isActive
        });

        return isProvisional && isActive;
      });

      console.log('[PROVISIONS CHECK] Has Provisional in Obj_LicenseKind with Active licenseKindStatus:', hasProvisionalInLicenseKind);
    }

    const result = hasProvisionalInLicense || hasProvisionalInLicenseKind;
    console.log('[PROVISIONS CHECK] Final result (either location has active Provisional):', result);

    return result;
  }

  /**
   * Filter the License Provisions question from visible questions if no Provisional license exists
   */
  private filterLicenseProvisionsQuestion(): void {
    console.log('[PROVISIONS FILTER] Checking if License Provisions question should be shown');

    // Find the index of the "License Provisions" question
    const provisionsQuestionIndex = this.qualifyingQuestions.findIndex(q =>
      q.toLowerCase().includes('license provisions')
    );

    if (provisionsQuestionIndex === -1) {
      console.log('[PROVISIONS FILTER] License Provisions question not found in question list');
      return;
    }

    console.log('[PROVISIONS FILTER] License Provisions question found at index:', provisionsQuestionIndex);
    console.log('[PROVISIONS FILTER] Question text:', this.qualifyingQuestions[provisionsQuestionIndex]);

    // Check if any license has Provisional type
    const hasProvisional = this.hasProvisionalLicenseType();

    if (!hasProvisional) {
      // Remove the License Provisions question from visible indices
      this.visibleQuestionIndices = this.visibleQuestionIndices.filter(i => i !== provisionsQuestionIndex);
      console.log('[PROVISIONS FILTER] No Provisional license found - hiding License Provisions question');
      console.log('[PROVISIONS FILTER] Updated visible indices:', this.visibleQuestionIndices);
    } else {
      console.log('[PROVISIONS FILTER] Provisional license found - showing License Provisions question');
    }
  }

  /**
   * Download license as PDF
   * Uses optimized iframe-based method (same as dashboard) for faster performance
   */
  downloadLicense(): void {
    const licenseData = this.dashboardData?.Obj_License?.[0];

    if (!licenseData || !licenseData.strLicenseNumber) {
      this.alertService.error('License information not found. Please ensure license data is loaded.');
      return;
    }

    console.log('[MODAL DOWNLOAD] ⏱️ Starting download process...');
    const startTime = performance.now();

    this.loaderService.show();

    const userId = this.sessionStorage.getItem('id');

    const apiStartTime = performance.now();
    this.httpService.post('/dashboard/license-pdf-download', { userId })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const apiEndTime = performance.now();
          console.log(`[MODAL DOWNLOAD] ✅ API call completed in ${(apiEndTime - apiStartTime).toFixed(2)}ms`);

          if (response?.data?.success && response.data.data?.length > 0) {
            const transformStartTime = performance.now();
            const apiData = response.data.data[0];
            const transformedLicenseData = this.transformApiLicensePdfData(apiData);
            const transformEndTime = performance.now();
            console.log(`[MODAL DOWNLOAD] 🔄 Data transformation completed in ${(transformEndTime - transformStartTime).toFixed(2)}ms`);

            const pdfStartTime = performance.now();
            console.log('[MODAL DOWNLOAD] 📄 Starting PDF generation (iframe method)...');
            this.renderLicensePdfClientSide(transformedLicenseData, pdfStartTime, startTime);
          } else {
            this.loaderService.hide();
            this.alertService.error('No license data found');
          }
        },
        error: (_error) => {
          const apiEndTime = performance.now();
          console.log(`[MODAL DOWNLOAD] ❌ API call failed after ${(apiEndTime - apiStartTime).toFixed(2)}ms`);
          this.loaderService.hide();
          this.alertService.error('Failed to fetch license data');
        }
      });
  }

  /**
   * Transform API license data for PDF generation
   * Matches dashboard component implementation exactly
   */
  private transformApiLicensePdfData(apiData: any): any {
    const licenseTypeLookup = this.getLookupMap('lookupLicenseTypes');
    const gradeLevelLookup = this.getLookupMap('lookupGradeLevel');
    const endorsementLookup = this.getLookupMap('lookupEndorsement');
    const licenseRequirementLookup = this.getLookupMap('lookupLicenseRequirementCategory');

    return {
      number: apiData.Obj_License[0].strLicenseNumber,
      nameOnLicense: apiData.Obj_License[0].strNameOnLicense,
      type: apiData.Obj_License[0].strLicenseType,
      kind: apiData.Obj_License[0].strLicenseKind,
      gradeLevel: gradeLevelLookup[apiData.Obj_License[0].strCdGrade] || apiData.Obj_License[0].strCdGrade,
      originalEndorsementIssueDate: apiData.Obj_License[0].dtIssueDate,
      endorsements: apiData.Obj_License[0].strEndorsement,
      issueDate: apiData.Obj_License[0].dtIssueDate,
      expirationDate: apiData.Obj_License[0].dtExpiryDate,
      status: 'Active',

      provisions: apiData.li_License_Provision.length ?
        apiData.li_License_Provision.map((prov: any) => ({
          provDescription: prov.strProvisionDetails || ' ',
          provKindOrEndorsement: prov.strSource || ' ',
          additionalText: prov.additional_text || ' ',
          dueDate: prov.dtDueDate,
        })) : [],

      renewalRequirements: apiData.li_License_Requirement.length ?
        apiData.li_License_Requirement.map((req: any) => ({
          requirement: req.strRequirement || ' ',
          dueDate: req.dtDueDate || ' ',
          reqDescription: req.req_description || ' ',
          category: req.strCategory || ' ',
          source: req.strSource || ' '
        })) : []
    };
  }

  /**
   * Get lookup map from localStorage
   * Matches dashboard component implementation
   */
  private getLookupMap(lookupKey: string): { [key: string]: string } {
    try {
      const lookup = localStorage.getItem(lookupKey);
      return lookup ? JSON.parse(lookup) : {};
    } catch (_error) {
      return {};
    }
  }

  /**
   * Build HTML string for license PDF
   * Copied from dashboard component for performance optimization
   */
  private buildLicensePdfHtml(licenseData: any): string {
    const fmt = (d: any) => d ? moment(d).format('MMM DD, YYYY') : '-';

    const licenseRow = `
    <tr>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${licenseData.type || ''} - ${licenseData.kind || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${fmt(licenseData.originalEndorsementIssueDate)}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${licenseData.endorsements || '-'}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${fmt(licenseData.issueDate)}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${fmt(licenseData.expirationDate)}</td>
    </tr>`;

    const provisionRows = (licenseData.provisions || []).map((item: any) => `
    <tr>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: left;">${item.provDescription || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${item.provKindOrEndorsement || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; font-weight: bold; text-align: center;">${item.additionalText || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${fmt(item.dueDate)}</td>
    </tr>`).join('');

    const requirementRows = (licenseData.renewalRequirements || []).map((item: any) => `
    <tr>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${item.category || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${item.source || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; font-weight: bold; text-align: center;">${item.requirement || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: left;">${item.reqDescription || ''}</td>
      <td style="border: 1px solid #e9ecef; padding: 8px; text-align: center;">${fmt(item.dueDate)}</td>
    </tr>`).join('');

    return `
    <html>
      <head>
        <style>
          @page { size: A4; margin: 0; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
          body { font-family: "Montserrat", sans-serif; color: #333; padding: 20mm; line-height: 1.4; margin: 0; }
          .header { text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; margin-bottom: 15px; }
          th { background-color: #f8f9fa !important; border: 1px solid #e9ecef; padding: 8px; font-size: 13px;  color: #333; font-weight: 600;}
          td { border: 1px solid #e9ecef; padding: 8px; font-size: 11px; font-weight: 400; color: #333; }
          .section-title { font-size: 16px; font-style: italic; color: #4a7ba7; font-weight: bolder; text-align: center; margin: 30px 0 20px 0; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 style="font-size: 30px; font-style: italic; color: #333; margin-bottom: 5px; font-weight: normal;">State of Nevada</h1>
          <h3 style="font-size: 20px; font-style: italic; color: #666; margin-bottom: 3px; font-weight: normal;">License for Educational Personnel</h3>
          <div>
            <p style="font-size: 16px; font-style: italic; color: #000; margin: 2px 0;">${licenseData.number || ''}</p>
            <p style="font-size: 16px; font-style: italic; margin: 2px 0;">This License Certifies that</p>
            <h2 style="font-size: 16px; font-style: italic; margin: 2px 0; font-weight: 800;">${licenseData.nameOnLicense || ''}</h2>
          </div>
          <p style="text-align: center; font-size: 16px; line-height: 1.6; margin: 20px 0 30px 0; font-weight: normal;">
            Has complied with the prescribed rules and regulations of the Commission on Professional Standards in Education and that the Superintendent of Public Instruction has granted this license which authorizes the holder to provide service in the schools of the State of Nevada in the following areas:
          </p>
        </div>

        <table>
          <thead>
            <tr>
              <th>License</th><th>Original Issue Date</th>
              <th>Endorsements</th><th>Issue Date</th><th>Expiration Date</th>
            </tr>
          </thead>
          <tbody>${licenseRow}</tbody>
        </table>

        <div class="section-title">Provisions to be satisfied</div>
        <table>
          <thead>
            <tr><th>Provisions</th><th>License Kind/Endorsement</th><th>Additional Text</th><th>Required Due Date</th></tr>
          </thead>
          <tbody>${provisionRows}</tbody>
        </table>

        <div class="section-title">License Requirements</div>
        <table>
          <thead>
            <tr><th>Category</th><th>Source</th><th colspan="2">License Requirement</th><th>Required Due Date</th></tr>
          </thead>
          <tbody>${requirementRows}</tbody>
        </table>
      </body>
    </html>`;
  }

  /**
   * Render license PDF using optimized iframe method
   * Copied from dashboard component for 55% faster performance
   */
  private renderLicensePdfClientSide(licenseData: any, pdfStartTime?: number, totalStartTime?: number): void {
    const fullHtml = this.buildLicensePdfHtml(licenseData);

    // A4 at 96 DPI = 794px — matches exactly what the browser uses for print layout
    const A4_PX = 794;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `position:fixed;left:-99999px;top:0;width:${A4_PX}px;height:1px;border:0;visibility:hidden;`;
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      this.loaderService.hide();
      this.alertService.error('Failed to generate license PDF');
      return;
    }

    iframeDoc.open();
    iframeDoc.write(fullHtml);
    iframeDoc.close();

    const captureFromIframe = () => {
      const body = iframeDoc.body;
      const contentHeight = body.scrollHeight;
      iframe.style.height = contentHeight + 'px';

      html2canvas(body, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        width: A4_PX,
        height: contentHeight,
        windowWidth: A4_PX,
        windowHeight: contentHeight
      }).then(canvas => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = 210;
        const pageHeight = 297;
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // If content spans multiple pages, split it
        let yOffset = 0;
        while (yOffset < imgHeight) {
          if (yOffset > 0) pdf.addPage();
          pdf.addImage(
            canvas.toDataURL('image/jpeg', 0.95),
            'JPEG',
            0, -yOffset,
            imgWidth, imgHeight
          );
          yOffset += pageHeight;
        }

        pdf.save(`License_${licenseData.number || 'document'}.pdf`);

        if (pdfStartTime) {
          const pdfEndTime = performance.now();
          console.log(`[MODAL DOWNLOAD] ✅ PDF generation completed in ${(pdfEndTime - pdfStartTime).toFixed(2)}ms`);
        }
        if (totalStartTime) {
          const totalEndTime = performance.now();
          console.log(`[MODAL DOWNLOAD] 🏁 TOTAL download process completed in ${(totalEndTime - totalStartTime).toFixed(2)}ms`);
        }

        this.loaderService.hide();
        document.body.removeChild(iframe);
      }).catch((_err: any) => {
        if (pdfStartTime) {
          const pdfEndTime = performance.now();
          console.log(`[MODAL DOWNLOAD] ❌ PDF generation failed after ${(pdfEndTime - pdfStartTime).toFixed(2)}ms`);
        }
        this.loaderService.hide();
        document.body.removeChild(iframe);
        this.alertService.error('Failed to generate license PDF');
      });
    };

    // Use onload if available, fallback to timeout
    if (iframe.contentWindow) {
      iframe.contentWindow.onload = () => setTimeout(captureFromIframe, 300);
    }
    setTimeout(captureFromIframe, 600);
  }

   private extractConditionalOptionConditions(): void {
    console.log('[CONDITIONS] Extracting conditions for 4 conditional options');

    // Reset all conditions
    this.shouldAllowSurrender = false;
    this.shouldAllowExtend = false;
    this.isExtendEnabled = false;
    this.shouldAllowAddEndorsement = false;
    this.shouldAllowResolveProvisions = false;

    if (!this.dashboardData) {
      console.log('[CONDITIONS] No dashboard data available');
      return;
    }

    // 1. SURRENDER - Check if license.status == 'Active'
    // Mirror dashboard's calculateLicenseStatus() logic exactly
    // Dashboard checks: Obj_License using strEndoStatus, Obj_LicenseKind using strLicenseKindStatus
    const licenseRows = Array.isArray(this.dashboardData?.Obj_License)
      ? this.dashboardData.Obj_License
      : (this.dashboardData?.Obj_License ? [this.dashboardData.Obj_License] : []);

    const licenseKindRows = Array.isArray(this.dashboardData?.Obj_LicenseKind)
      ? this.dashboardData.Obj_LicenseKind
      : (this.dashboardData?.Obj_LicenseKind ? [this.dashboardData.Obj_LicenseKind] : []);

    console.log('[SURRENDER DEBUG] licenseRows:', licenseRows);
    console.log('[SURRENDER DEBUG] licenseKindRows:', licenseKindRows);

    // Mirror dashboard logic: Check Obj_License rows using strEndoStatus (NOT strLicenseKindStatus!)
    const licenseRowsInactive = licenseRows.length > 0
      ? licenseRows.every((row: any) => {
          const status = (row?.strEndoStatus || '').toLowerCase();  // ← Use strEndoStatus for Obj_License
          console.log('[SURRENDER DEBUG] License row - strEndoStatus:', row?.strEndoStatus, 'computed status:', status);
          // Row is inactive if: (1) status='inactive' OR (2) status='active' but expired
          return status === 'inactive' || (status === 'active' && this.isLicenseExpired(row?.dtExpiryDate));
        })
      : true;

    // Mirror dashboard logic: Check Obj_LicenseKind rows using strLicenseKindStatus
    const licenseKindRowsInactive = licenseKindRows.length > 0
      ? licenseKindRows.every((row: any) => {
          const status = (row?.strLicenseKindStatus || '').toLowerCase();  // ← Use strLicenseKindStatus for Obj_LicenseKind
          console.log('[SURRENDER DEBUG] LicenseKind row - strLicenseKindStatus:', row?.strLicenseKindStatus, 'computed status:', status);
          // Row is inactive if: (1) status='inactive' OR (2) status='active' but expired
          return status === 'inactive' || (status === 'active' && this.isLicenseExpired(row?.dtExpiryDate));
        })
      : true;

    // Mirror dashboard logic: Return 'Active' if NOT all rows are inactive (i.e., if ANY row has active status)
    this.shouldAllowSurrender = !(licenseRowsInactive && licenseKindRowsInactive);
    console.log('[CONDITIONS] shouldAllowSurrender:', this.shouldAllowSurrender);
    console.log('[SURRENDER DEBUG] licenseRowsInactive:', licenseRowsInactive, 'licenseKindRowsInactive:', licenseKindRowsInactive);

    // 2. EXTEND - Check boolHideExtend and boolEnableExtend from dashboard data
    const licenseDataExists = this.dashboardData?.Obj_License?.[0];
    this.shouldAllowExtend = licenseDataExists && !this.dashboardData?.boolHideExtend;
    this.isExtendEnabled = !!this.dashboardData?.boolEnableExtend;
    console.log('[CONDITIONS] shouldAllowExtend:', this.shouldAllowExtend, 'isExtendEnabled:', this.isExtendEnabled, '(hasLicense:', !!licenseDataExists, ')');

    // 3. ADD ENDORSEMENT - Check for approved Regular-Initial app AND license not expiring within 3 months
    const hasApprovedRegularInitial = this.dashboardData?.li_Application?.some((app: any) =>
      app.strAppType?.toLowerCase() === 'regular - initial' &&
      app.strAppStatus?.toLowerCase().includes('approved')
    );

    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    const licenseDataForEndorsement = this.dashboardData?.Obj_License?.[0];
     const licenseRowsForEndorsement = Array.isArray(this.dashboardData?.Obj_License)
      ? this.dashboardData.Obj_License
      : (this.dashboardData?.Obj_License ? [this.dashboardData.Obj_License] : []);
    
    const latestLicenseData = licenseRowsForEndorsement
      .sort((a: any, b: any) => new Date(b.dtExpiryDate).getTime() - new Date(a.dtExpiryDate).getTime())[0] || {};
    
    const isExpiringSoon = latestLicenseData?.dtExpiryDate
      ? new Date(latestLicenseData.dtExpiryDate) <= threeMonthsFromNow
      : false;

    this.shouldAllowAddEndorsement = hasApprovedRegularInitial && !isExpiringSoon;
    console.log('[CONDITIONS] shouldAllowAddEndorsement:', this.shouldAllowAddEndorsement,
      '(hasApprovedRegularInitial:', hasApprovedRegularInitial, ', isExpiringSoon:', isExpiringSoon, ')');
    // 4. RESOLVE PROVISIONS - Check if hasLicenseProvisions (li_License_Provision array has items) AND license exists
    const licenseProvisions = this.dashboardData?.li_License_Provision || [];
    // const licenseDataExists = this.dashboardData?.Obj_License?.[0];
    this.shouldAllowResolveProvisions = licenseDataExists && Array.isArray(licenseProvisions) && licenseProvisions.length > 0;
    console.log('[CONDITIONS] shouldAllowResolveProvisions:', this.shouldAllowResolveProvisions,
      '(hasLicense:', !!licenseDataExists, ', provisions count:', licenseProvisions.length, ')');
    // Now add the 4 conditional options to applicationTypes if their conditions are met
    this.addConditionalOptionsToApplicationTypes();
  }

  /**
   * Add the 4 conditional options to applicationTypes array based on their visibility conditions
   */
  private addConditionalOptionsToApplicationTypes(): void {
    console.log('[ADD OPTIONS] Starting to save conditional options');

    // Define the 4 conditional options in priority order
    const conditionalOptions = [
      { key: 'add-endorsement', value: 'Add Endorsement', condition: this.shouldAllowAddEndorsement },
      { key: 'resolve-provisions', value: 'Resolve Provisions/Requirements', condition: this.shouldAllowResolveProvisions },
      { key: 'extend', value: 'Extend License', condition: this.shouldAllowExtend && this.isExtendEnabled },
      { key: 'surrender', value: 'Surrender Endorsements', condition: this.shouldAllowSurrender }
    ];

    // SAVE each conditional option if its condition is met (don't add to applicationTypes yet)
    this.savedConditionalOptions = [];
    conditionalOptions.forEach(option => {
      if (option.condition) {
        this.savedConditionalOptions.push(option);
        console.log('[ADD OPTIONS] Saved option for later restoration:', option.value);
      }
    });

    console.log('[ADD OPTIONS] Total saved conditional options:', this.savedConditionalOptions.length);
    console.log('[ADD OPTIONS] Saved options:', this.savedConditionalOptions);
  }


  private restoreConditionalOptions(): void {
    if (this.savedConditionalOptions.length === 0) {
      console.log('[RESTORE OPTIONS] No saved conditional options to restore');
      return;
    }

    console.log('[RESTORE OPTIONS] Restoring saved conditional options');
    console.log('[RESTORE OPTIONS] applicationTypes before restore:', this.applicationTypes.length, 'items:', this.applicationTypes.map(t => t.value || t.key));

    // Add each saved conditional option if it doesn't already exist

    const addEndorsementOption = this.savedConditionalOptions.find(opt => opt.key === 'add-endorsement');
    const nameChangeIndex = this.applicationTypes.findIndex(t => t.value === 'Name Change');

    if (addEndorsementOption && nameChangeIndex > -1) {
      // Check if Add Endorsement doesn't already exist in applicationTypes
      if (!this.applicationTypes.find(t => t.key === 'add-endorsement')) {
        // Insert Add Endorsement before Name Change
        this.applicationTypes.splice(nameChangeIndex, 0, addEndorsementOption);
        console.log('[RESTORE OPTIONS] Added "Add Endorsement" before "Name Change"');
      }
    }
    this.savedConditionalOptions.forEach(option => {
      if (option.key !== 'add-endorsement' && !this.applicationTypes.find(t => t.key === option.key)) {
        this.applicationTypes.push(option);
        console.log('[RESTORE OPTIONS] Restored option:', option.value);
      }
    });

    console.log('[RESTORE OPTIONS] applicationTypes after restore:', this.applicationTypes.length, 'items:', this.applicationTypes.map(t => t.value || t.key));
  }

  private isLicenseExpired(expirationDate: string | Date | null | undefined): boolean {
    if (!expirationDate) {
      return false;
    }

    try {
      const expDate = new Date(expirationDate);
      const today = new Date();

      // Strip time from both dates for fair comparison
      expDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return today > expDate;
    } catch (_error) {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.loaderService.hide();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
